import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as rt from "@/lib/backend/reports.generated.types";
import * as t2 from "@/lib/simulationReportModels";
import * as Auth from "@/lib/auth";
import * as BusinessLogic from "@/lib/businessLogic";
import local_settings, { FIELD_LAST_PLANT } from "@/lib/localSettings";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import { buildQuickSimSyncRequestHeaders } from "../../lib/quickSimContext";
import {
    shiftNumber,
    TIME_RANGES,
    dateFromWeekAndShift,
    toISODateString,
    uuidv4,
    SHIFT_CONSTS,
    getDateOfISOWeekShift,
    deepClone,
    isStringNumber,
} from "@/lib/util";
import { addMaterialColors, getOrdersWithZeroDuration } from "@/lib/ganttData";
import {
    LINE_TAGS,
    INSIGHT_TYPES,
    LINE_GROUP_TAGS,
    LINE_GROUP_TAGS_ACCESS,
    ORDER_TAGS,
    PLANNING_META_OPTIMIZATION,
    PLANT_TAGS_ACCESS,
    ORDER_TAGS_ACCESS,
    InsightTypesEnum,
    PlanningMetaOptimizationEnum,
} from "@/lib/ManufacturingTags.generated";
import { LinesOrdersMaps } from "../linesOrders/mappers";
import { prepareGanttChart2, prepareGanttChartInsights } from "@/lib/ganttChartUtils";
import { translate, getLang } from "@/modules/shared/IntlProviderWrapper";
import { SET_TRANSLATIONS_MATERIALS } from "@/store/reducers/translationsMaterialsReducer";
import { store } from "@/index";
import {
    cancelPlanningTableLoadWallTimeSession,
    startPlanningTableLoadWallTimeSession,
} from "../../lib/planningTableLoadWallTimeSession";
import {
    PLANNING_TABLE_RUM_DURATION_VITALS,
    recordPlanningTableRumDurationVital,
} from "../../lib/planningTableRumVitals";
import { JOB_STATUS } from "@/lib/CommonConsts.generated";
import {
    GUI_LOCAL_STORAGE,
    QUICK_SIM_FIXED_CACHE_HASH,
    PLANNING_EXPLANATION_TYPE,
    PLANNING_WARNING_TYPE,
    ORDER_STATUS,
} from "@/lib/ManufacturingConsts.generated";
import { PRODUCTION_VERSION_COLUMN } from "../../saveToSap/OrderChangesModal";
import { LOCKED_LINE_ID } from "../../lineOrderList/LineFilter";
// reducers
import { REPORT_LOADING_ACTION_NAME } from "../report";
import { UPDATE_REPORT_ACTION_NAME2, SET_RESCHEDULE_ORDER_BUNDLE, cloneReportParameters } from "../common";
import { countRowsOnGantt, countUnscheduledLinesOnGantt } from "../gantt/properties";
import { UPDATE_PARALLEL_REPORT_PARAMETERS } from "../report/parallelReport";
import { GanttChartSource, SKIP_STOCK_REPORT, zoomGanttChart } from "../gantt/properties";

import { UPDATE_CURRENT_LINE_STATUSES_ACTION_NAME, UPDATE_INSIGHTS_ACTION_NAME } from "../planningTable/insights";
import {
    LINE_ORDERS_LOADING_ACTION_NAME,
    UPDATE_LINES_ORDERS_ACTION_NAME,
    SET_IGNORED_ORDERS_ACTION_NAME,
    SET_UNSCHEDULED_ORDERS_ACTION_NAME,
    SET_FILTERED_OUT_ORDERS,
    UPDATE_ALL_ORDERS_RECOMMEND_PLAN,
    SET_CHUNKED_ORDERS,
    UPDATE_FIXED_EXTRA_LINES,
} from "../linesOrders";
import { UPDATE_MATERIALS } from "../gantt/ganttChart";
import {
    SET_RECOMMEND_PLAN_RUNNING,
    SET_RECOMMEND_PLAN_MODAL,
    SET_RECOMMEND_PLAN_LINE_PLANS,
    SET_REPORT_TYPE_TITLE_ACTION_NAME,
    SET_ERROR,
    SET_RECOMMEND_PLAN_ERROR,
    SET_ADVANCED_ORDER_MENU,
    SET_PLANNING_DETAILS_SELECTED_VIEW,
    SET_HIGHLIGHTED_ORDERS,
    SET_RECOMMEND_WARNINGS,
    SET_LAST_CLICKED_ORDER_UUID,
    CLEAR_MULTISELECT_ORDERS,
} from "../planningTable";
import { RESET_FILTERS, SET_SELECTED_LINE_UUIDS } from "../filters/filters";
import { ORDER_TYPE, PLANNING_RESOURCE_TYPES, ORDER_GENERATED_BY } from "@/lib/ManufacturingConsts.generated";
import { UPDATE_RECOMMEND_PLAN, UPDATE_REPORT_UUID } from "../planningTable/recommendPlan";
import { SIMULATION_TYPES, SET_SELECTED_STOCK_REQUIREMENT } from "../common";
import { SET_ORDER_TABLE_PRODUCTION_MODAL } from "../common";
import { SET_STOCK_REQ_FILTERS_MATERIALS } from "../filters/stockRequirementsFilters";
import { TypeFilterValues } from "@/modules/shared/planningTable/typeFilterConsts";
import { SET_MOUSE_CURSOR } from "../../interaction/MouseCursor";
import PlanningTableColumns from "../../planningDetails/PlanningTableColumns";
import { getAlternateCampaignGroups, getCampaignGroupsReference, CampaignGroup } from "../linesOrders/orderCampaign";

import type { ReduxState } from "../reducers";
import type { FilterLinesOrders } from "@/lib/ganttData";
import type { CursorKeys } from "../../interaction/MouseCursor";
import type { SetStockReqFilterMaterials } from "../filters/stockRequirementsFilters";
import type { ResetDraggedOrder, SetRescheduleOrderAction } from "../common";
import type {
    SetRecommendPlanLinePlans,
    SetRecommendPlanRunningAction,
    SetReportTypeTitleAction,
    SetErrorAction,
    SetRecommendPlanErrorAction,
    SetAdvancedOrderMenuAction,
    ResetAdvancedOrderMenu,
    AdvancedOrderMenuState,
    SetRecommendExplanationsAction,
    SetHighlightedOrders,
    SetRecommendWarningsAction,
    SetLastClickedOrderUuid,
    SetPlanningDetailsSelectedView,
    ClearMultiselectOrdersAction,
    SetRecommendPlanModalAction,
} from "../planningTable";
import type { ReportLoadingAction, AddExtraLinesAction } from "../report";
import type {
    UpdateParallelReportAction,
    ParallelReportLoading,
    UpdateParallelReportParameters,
} from "../report/parallelReport";
import type { UpdateInsightsAction, UpdateCurrentLineStatusesAction } from "../planningTable/insights";
import type {
    LineOrders,
    IOrderProducedModelEx,
    LoadingAction,
    UpdateLinesOrdersAction,
    SetIgnoredOrdersAction,
    SetUnscheduledOrdersAction,
    SetFilteredOutAction,
    OrderHighlights,
    UpdateLinesOrdersPlanAction,
    LoadVersionAction,
    UpdateAllOrdersRecommendPlanAction,
    UpdateFixedExtraLinesAction,
    UpdateChunkedOrderAction,
} from "../linesOrders";
import type { UpdateMaterialsAction } from "../gantt/ganttChart";
import type {
    ResetGanttAction,
    QuickSimArgs,
    ReportParameters,
    SetSelectedStockRequirement,
    UpdateReportAction,
    UpdateReport2Action,
    SetRescheduleOrderBundleAction,
    ValidOrder,
    ValidLineOrders,
} from "../common";
import type { ILineData, IPlanLine, IPlanWarning } from "@/lib/backend/manufacturing2.generated.types";
import type { PlanningTableFiltersState, ResetFiltersAction, SetSelectedLineUuidsAction } from "../filters/filters";
import type { PropertiesState, GanttChartSourceTypes, SetSkipStockReport } from "../gantt/properties";

import { SHIFTS_IN_WEEK, SHIFTS_PER_DAY } from "../../ganttChart/constants";
import { ValidLinesOrdersHelper } from "../linesOrders/validLinesOrdersHelper";
import { SET_PAST_ORDERS_ALERTS } from "../planningTable/alertsReducer";
import {
    CUSTOM_FILTER_ENTITIES,
    CUSTOM_FILTER_INPUT_TYPES,
    CUSTOM_FILTER_MATCH,
} from "../../lineOrderList/filters/CustomFilter";

import type { SetTranslationsMaterials } from "@/store/reducers/translationsMaterialsReducer";
import type { Dispatch, Store } from "redux";
import type { SetOrderTableProductionModalAction } from "../common";
import type {
    UpdateReportRecommendPlanAction,
    UpdateRecommendPlanAction,
    RecommendPlanParamsAndCriteria,
} from "../planningTable/recommendPlan";
import type { StockRequirement, UpdateStockRequirements } from "../planningTable/stockRequirements";
import type { UpdateStockRequirementsHeightAction, UpdatePlanningTableWidthAction } from "../gantt/properties";
import type { StockRequirementsFiltersState } from "../filters/stockRequirementsFilters";
import type { InsightsMapStockRequirements } from "../planningTable/insights";
import type { ChunkedOrder, ChunkedOrdersAction } from "../linesOrders";
import type { SetPastOrdersAlertsAction } from "../planningTable/alertsReducer";
import type { ConvertOperationData, ConvertOrderData } from "../linesOrders";
import type {
    CustomFilterEntity,
    CustomFilterInputType,
    CustomFilterMatch,
} from "../../lineOrderList/filters/CustomFilter";

export const VERSION_TYPE = {
    initial: "initial",
    current: "current",
    version: "version",
};

export const AUTOMATIC_PLANNING_ERROR_FALLBACK = "Automatic planning failed.";

export type RunRecommendPlanParameters = {
    freeze_orders: t.IRunLinegroupPlanningFreezeOrder[];
    line_group_uuid: string;
    line_uuids: string[];
    lines_orders: LineOrders[];
    order_quanity_produced_map: Map<string, number>;
    order_time_start_map?: Map<string, number>;
    original_lines_orders: LineOrders[];
    plan_after_frozen_orders: boolean;
    planning_weeks: number;
    rearrange_orders_map: RearrangeOrdersMap;
    state: RecommendPlanParamsAndCriteria;
    unscheduled_orders: LineOrders[];
};

export type RunBackwardPlanParameters = {
    line_group_uuid: string;
    line_uuids: string[];
    lines_orders: LineOrders[];
    planning_weeks: number;
    state: RecommendPlanParamsAndCriteria;
    unscheduled_orders: LineOrders[];
    report_data: t2.IReportModelEx | null;
};

type ReportRes = {
    quick_report: t2.IReportModelEx | null;
    insights: t.IEventDataEx[] | null;
    error: string | null;
    extra_lines: t.IExtraLines[] | null;
    filtered_orders: FilterLinesOrders[];
    translations?: t.ITranslations;
    cache_hash?: CacheParams;
};

export type FilterValidLinesOrders = {
    valid_lines: ValidLineOrders[];
    valid_orders: ValidOrder[];
};

export type LineOrdersLoadActions =
    | LoadingAction
    | UpdateLinesOrdersAction
    | SetIgnoredOrdersAction
    | SetUnscheduledOrdersAction
    | SetTranslationsMaterials
    | UpdateMaterialsAction
    | ChunkedOrdersAction
    | SetPastOrdersAlertsAction;

type CustomOrderField = { id: string; title: string; views: string[]; type?: string };

type ExtraData = {
    offset_material_external_id: string;
    offset_material_title: string;
    offset_previous_operation: boolean;
    material_external_id: string;
    material_title: string;
};

export class PropertiesLogic {
    static getNumShiftsFromDayTag = (
        source_type: GanttChartSourceTypes,
        line_group_uuid: string,
        is_parallel: boolean = false
    ): number => {
        if (is_parallel) {
            return (
                BusinessLogic.getLineGroupTagInt(
                    line_group_uuid,
                    LINE_GROUP_TAGS.planning_table_save_to_erp_weeks,
                    16
                ) * SHIFTS_IN_WEEK
            );
        }
        const tags = BusinessLogic.getLineGroupTags(line_group_uuid);
        if (PropertiesLogic.isPlanningTable(source_type)) {
            return LINE_GROUP_TAGS_ACCESS.planning_table_weeks(tags) * SHIFTS_PER_DAY * 7;
        }
        if (source_type === GanttChartSource.microplan) {
            return LINE_GROUP_TAGS_ACCESS.microplan_gantt_number_of_days(tags) * SHIFTS_PER_DAY;
        }
        if (source_type === GanttChartSource.line_planning) {
            return LINE_GROUP_TAGS_ACCESS.microplan_gantt_number_of_days(tags) * SHIFTS_PER_DAY;
        }

        return LINE_GROUP_TAGS_ACCESS.gantt_number_of_days(tags) * SHIFTS_PER_DAY;
    };

    static getTopPadding = (properties: PropertiesState, source_type?: GanttChartSourceTypes) => {
        if (source_type === GanttChartSource.stock_requirements || source_type === GanttChartSource.gantt_unscheduled) {
            return 0;
        }
        return properties.top_padding;
    };

    static hasBarsStart = (source_type: GanttChartSourceTypes) => {
        return !(source_type === GanttChartSource.stock_requirements || source_type === GanttChartSource.microplan);
    };

    static isPlanningTable = (source_type: GanttChartSourceTypes) => {
        return (
            source_type === GanttChartSource.planning_table_parallel || source_type === GanttChartSource.planning_table
        );
    };

    static isUnscheduled = (source_type: GanttChartSourceTypes) => {
        return source_type === GanttChartSource.gantt_unscheduled;
    };

    static getCurrentShift = (properties: PropertiesState): number => {
        if (properties.current_shift === null) {
            console.error("PropertiesLogic: current shift is not set");
        }
        return properties.current_shift || 0;
    };

    static getStartDay = (properties: PropertiesState, default_value: number = 0): number => {
        if (properties.start_day === null) {
            console.error("PropertiesLogic: Start day is not set");
        }
        return properties.start_day || default_value;
    };

    static getWidth = (properties: PropertiesState): number => {
        if (properties.width === null) {
            console.error("PropertiesLogic: Width is not set");
        }
        return properties.width || 0;
    };

    static getShiftWidth = (properties: PropertiesState): number => {
        if (properties.shift_width === null) {
            console.error("PropertiesLogic:  Shift width is not set");
        }
        return properties.shift_width || 0;
    };

    static getYear = (properties: PropertiesState): number => {
        if (properties.year === null) {
            console.error("PropertiesLogic:  Week is not set");
        }
        return properties.year || 0;
    };

    static getWeek = (properties: PropertiesState): number => {
        if (properties.week === null) {
            console.error("PropertiesLogic:  Week is not set");
        }
        return properties.week || 0;
    };

    static getHeight = (properties: PropertiesState, source_type: GanttChartSourceTypes) => {
        if (source_type === GanttChartSource.gantt_unscheduled) {
            return properties.unscheduled_height;
        }
        if (source_type === GanttChartSource.stock_requirements) {
            return properties.stock_requirements_height;
        }

        return properties.height;
    };

    static getNumLines = (properties: PropertiesState, source_type: GanttChartSourceTypes) => {
        if (source_type === GanttChartSource.gantt_unscheduled) {
            return properties.num_unscheduled_lines;
        }
        return properties.count_non_empty_lines;
    };
}

export class ReportCache {
    static parallel_report_cache_hash: string | null = null;
    static report_cache_hash: string | null = null;

    static isFixedCacheHash = () => {
        // special case for development mode
        const is_dev_env = BusinessLogic.getSysFlag("env") === "dev";
        // if in dev mode, check if we have fixed cache hash
        return is_dev_env && local_settings.getItem(GUI_LOCAL_STORAGE.fixed_quick_sim_cache_hash) === "true";
    };

    static removeCacheHash = () => {
        ReportCache.parallel_report_cache_hash = null;
        ReportCache.report_cache_hash = null;
    };

    static setCacheHash = (project_parallel_orders: boolean): string => {
        // if not fixed, generate uuid
        const hash = this.isFixedCacheHash()
            ? project_parallel_orders
                ? QUICK_SIM_FIXED_CACHE_HASH.tralala
                : QUICK_SIM_FIXED_CACHE_HASH.hopsasa
            : uuidv4();

        if (project_parallel_orders) {
            ReportCache.parallel_report_cache_hash = hash;
        } else {
            ReportCache.report_cache_hash = hash;
        }

        return hash;
    };

    static getCacheHash = (project_parallel_orders: boolean): CacheParams => {
        let cache_hash: string | null = null;
        if (project_parallel_orders) {
            cache_hash = ReportCache.parallel_report_cache_hash;
        } else {
            cache_hash = ReportCache.report_cache_hash;
        }

        if (cache_hash) {
            return { cache_hash, cache_reset: false };
        }

        return { cache_hash: this.setCacheHash(project_parallel_orders), cache_reset: true };
    };
}

type CacheParams = {
    cache_hash: string;
    cache_reset: boolean;
};

export type SimulationActionsTypes =
    | UpdateReportAction
    | UpdateInsightsAction
    | ReportLoadingAction
    | ResetGanttAction
    | SetErrorAction
    | AddExtraLinesAction
    | SetFilteredOutAction
    | UpdateParallelReportAction
    | ParallelReportLoading
    | UpdateParallelReportParameters
    | SetTranslationsMaterials
    | UpdateReport2Action
    | UpdateReportRecommendPlanAction
    | ResetDraggedOrder
    | LoadVersionAction
    | UpdatePlanningTableWidthAction;

export const INSIGHT_DIFF_TYPE = {
    solved: "solved",
    same: "same",
    new: "new",
};

export type InsightDiff = {
    diff_type: string;
    current_insight?: rt.IEventDataEx;
    original_insight?: rt.IEventDataEx;
};

type UpdateParametersRet = {
    report_parameters: ReportParameters;
    noproductionversion_order_uuids: string[];
};

export type GenerateParallelReportRet = {
    warnings: Warning[];
    report_uuid: string;
};

export type Warning = {
    order_uuid: string;
    field: typeof PRODUCTION_VERSION_COLUMN;
    warning: string;
};

type IGetCompatibleProductionVersionsOp = {
    op_uuid: string;
    order_external_id: string;
    fixed_line_uuid?: string;
};

export class ReportLogic {
    /** Utility method for waiting on job */
    static waitForExport = async (uuid: string): Promise<rt.IRunPlanTableExportResult> => {
        const CHECK_INTERVAL = 3 * TIME_RANGES.SECOND;
        const MAX_WAIT_TIME = 60 * TIME_RANGES.MINUTE;

        return new Promise((resolve, reject) => {
            let counter = 0;
            const loopCall = async (): Promise<void> => {
                try {
                    const res = await getBackend2().common.getJobStatus({ id: uuid });
                    if (!res) {
                        reject(new Error("Job with given uuid not found"));
                        return;
                    }
                    if (res.status === JOB_STATUS.errored) {
                        reject(new Error("Job failed with error: " + res.status_msg));
                        return;
                    }
                    if (res.status === JOB_STATUS.ended) {
                        const res2 = await getBackend2().common.getJobInfo({ id: uuid });
                        resolve(res2.result);
                        return;
                    }

                    // check if we have been waiting for too long
                    counter++;
                    if (counter * CHECK_INTERVAL >= MAX_WAIT_TIME) {
                        reject(new Error("Job time-out: " + uuid));
                        return;
                    }

                    setTimeout(loopCall.bind(this), CHECK_INTERVAL);
                } catch (err) {
                    console.error(err);
                    reject(err);
                    return;
                }
            };
            setTimeout(loopCall.bind(this), CHECK_INTERVAL);
        });
    };

    static updateParametersProductionVersions = async (parameters: ReportParameters): Promise<UpdateParametersRet> => {
        const prod_ver_operations: IGetCompatibleProductionVersionsOp[] = [];

        const line_uuids: string[] = [];
        const order_external_id_uuid_map = new Map<string, string>();
        const order_refs_map = new Map<string, IOrderProducedModelEx>();

        for (const line_orders of parameters.config.lines_orders) {
            line_uuids.push(line_orders.line_uuid);
            for (const order of line_orders.orders) {
                order_external_id_uuid_map.set(order.external_id, order.uuid);
                order_refs_map.set(order.uuid, order);
                prod_ver_operations.push({
                    op_uuid: order.uuid,
                    order_external_id: order.external_id,
                    fixed_line_uuid: line_orders.line_uuid,
                });
            }
        }

        const prod_ver_params = {
            constraints: {
                fix_unplanned_ops: true,
                ignore_operation_fix_line_constraint: false,
                planned_line_uuids: undefined,
                planned_ord_same_bom: true,
                planned_ord_same_op_structure: true,
                prod_ord_same_bom: true,
                prod_ord_same_op_structure: true,
                prod_ord_can_change_line: false,
            },
            operations: prod_ver_operations,
        };

        const res = await getBackend2().manufacturing.getCompatibleProdVers({
            params: prod_ver_params,
        });

        const noproductionversion_order_uuids = new Set<string>();

        for (const order_prod_ver of res.orders_prod_vers) {
            const order_uuid = order_external_id_uuid_map.get(order_prod_ver.order_external_id);
            if (order_uuid) {
                const order = order_refs_map.get(order_uuid);
                if (order && order_prod_ver.production_versions.length > 0) {
                    if (
                        order.tags[ORDER_TAGS.production_version] !==
                        order_prod_ver.production_versions[0].prod_ver_external_id
                    ) {
                        console.log(
                            "Different prod version for order: ",
                            order.external_id,
                            order.tags[ORDER_TAGS.production_version],
                            order_prod_ver.production_versions[0].prod_ver_external_id
                        );
                    }
                    order.tags[ORDER_TAGS.production_version] =
                        order_prod_ver.production_versions[0].prod_ver_external_id;
                } else {
                    noproductionversion_order_uuids.add(order_uuid);
                }
            } else {
                console.error("Unable to find order uuid for calculated prod vers!", order_uuid);
            }
        }
        return {
            report_parameters: parameters,
            noproductionversion_order_uuids: [...noproductionversion_order_uuids],
        };
    };

    static generateWarnings = (noversion: string[]): Warning[] => {
        const order_warnings: Warning[] = [];
        return order_warnings.concat(
            noversion.map(uuid => {
                return {
                    order_uuid: uuid,
                    field: PRODUCTION_VERSION_COLUMN,
                    warning: translate("common.no_production_version", "Could not find production version"),
                };
            })
        );
    };

    static generateParallelReportUuidWarning = async (
        parameters: ReportParameters,
        initialReportUuid?: string | null
    ): Promise<GenerateParallelReportRet | undefined> => {
        if (!parameters || !parameters.config) {
            return;
        }

        const { report_parameters, noproductionversion_order_uuids } =
            await ReportLogic.updateParametersProductionVersions(cloneReportParameters(parameters));

        const warnings = ReportLogic.generateWarnings(noproductionversion_order_uuids);
        parameters = report_parameters;
        parameters.config.project_parallel_orders = true;
        parameters.quickSimContext = initialReportUuid != null ? "plan-initialize-export" : "plan-initialize-long";
        const quick_sim = await ReportLogic.quickSimulation(parameters);

        if (quick_sim && quick_sim.quick_report) {
            return {
                warnings,
                report_uuid: quick_sim.quick_report.uuid,
            };
        }

        if (quick_sim.error) {
            throw Error(quick_sim.error);
        }
    };

    static getEnabledItems = (orders: IOrderProducedModelEx[]): IOrderProducedModelEx[] => {
        return orders.filter((l: IOrderProducedModelEx) => !l.skip_sim);
    };

    static mergeReports = (quick_report: t2.IReportModelEx, full_report: t2.IReportModelEx): t2.IReportModelEx => {
        const line_shift_features_map = new Map<string, rt.ISimulationReportOrderLineShiftFeature>();
        for (const line of quick_report.result.line_shift_features) {
            line_shift_features_map.set(line.line, line);
        }

        const new_line_shift_features: rt.ISimulationReportOrderLineShiftFeature[] = [];
        for (const shift_line of full_report.result.line_shift_features) {
            const new_line = line_shift_features_map.get(shift_line.line);
            if (new_line) {
                new_line_shift_features.push(new_line);
            } else {
                new_line_shift_features.push(shift_line);
            }
        }
        quick_report.result.line_shift_features = new_line_shift_features;

        const lines_map = new Map<string, t2.ISimulationReportOrderLineEx[]>();
        for (const line of quick_report.result.orders) {
            const new_lines = lines_map.get(line.line);
            if (new_lines) {
                lines_map.set(line.line, [...new_lines]);
            } else {
                lines_map.set(line.line, [line]);
            }
        }

        let sim_lines: t2.ISimulationReportOrderLineEx[] = [];
        const already_inserted = new Set();
        for (const line of full_report.result.orders) {
            if (already_inserted.has(line.line)) {
                continue;
            }
            const new_line = lines_map.get(line.line);
            if (new_line) {
                sim_lines = sim_lines.concat(new_line);
            } else {
                sim_lines = sim_lines.concat([line]);
            }
            already_inserted.add(line.line);
        }
        quick_report.result.orders = sim_lines;

        return quick_report;
    };

    static quickSimulationAction = async (
        parameters: ReportParameters,
        dispatch: (arg: SimulationActionsTypes) => void,
        full_report?: t2.IReportModelEx | null
    ) => {
        try {
            // first generate parallel report uuid, to enable saving of the plan
            ReportLoadingEventHandler.showLoading(true);
            dispatch({ type: REPORT_LOADING_ACTION_NAME, data: translate("common.running_simulation") });
            dispatch({ type: UPDATE_PARALLEL_REPORT_PARAMETERS, data: parameters });
            const quick_sim = await ReportLogic.quickSimulation(parameters, full_report);
            ReportLoadingEventHandler.showLoading(false);
            const error = quick_sim.error || null;

            if (error) {
                dispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
                dispatch({ type: SET_ERROR, data: error });
                console.error(error);
            }

            if (quick_sim && quick_sim.quick_report && quick_sim.insights) {
                if (quick_sim.translations && quick_sim.translations.materials) {
                    store.dispatch({ type: SET_TRANSLATIONS_MATERIALS, data: quick_sim.translations.materials });
                }
                const quick_report: t2.IReportModelEx = quick_sim.quick_report;
                const insights: t.IEventDataEx[] = quick_sim.insights;
                const line_group_tags = BusinessLogic.getLineGroupTags(parameters.config.line_group_uuid);
                const show_active_operations = LINE_GROUP_TAGS_ACCESS.gantt_show_active_operations(line_group_tags);
                if (show_active_operations) {
                    const lines = parameters.config.lines_orders.map(line => line.line_uuid);
                    await BusinessLogic.addCurrentlyActiveStatuses(quick_report, lines);
                }

                if (parameters.recommend_plan_uuid) {
                    dispatch({
                        type: UPDATE_REPORT_UUID,
                        data: {
                            plan_uuid: parameters.recommend_plan_uuid,
                            report_uuid: quick_report.uuid,
                        },
                    });
                }

                dispatch({
                    type: UPDATE_INSIGHTS_ACTION_NAME,
                    data: {
                        insights,
                        insight_types: parameters.selected_insight_types,
                    },
                });

                dispatch({
                    type: UPDATE_REPORT_ACTION_NAME2,
                    data: {
                        quick_report,
                        extra_lines: quick_sim.extra_lines,
                    },
                });

                if (quick_sim.filtered_orders.length > 0) {
                    dispatch({ type: SET_FILTERED_OUT_ORDERS, data: quick_sim.filtered_orders });
                }

                zoomGanttChart(null, GanttChartSource.planning_table, dispatch);

                dispatch({ type: SET_ERROR, data: error });
            } else {
                dispatch({ type: SET_ERROR, data: error });
            }
            dispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
        } catch (e) {
            console.error("quickSimulationAction: ", e);
            dispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
            dispatch({ type: SET_ERROR, data: (e as Error).message });
        }
    };

    static quickSimulation = async (
        parameters: ReportParameters,
        full_report?: t2.IReportModelEx | null
    ): Promise<ReportRes> => {
        const { config } = parameters;
        const {
            plant_uuid,
            line_group_uuid,
            title,
            lines_orders,
            project_parallel_orders,
            unscheduled_orders,
            skip_stock,
            ignore_orders_status,
        } = config;

        const additional_tags: { simulation_type?: SIMULATION_TYPES } = {};
        if (config.simulation_type) {
            additional_tags.simulation_type = config.simulation_type as SIMULATION_TYPES;
        }

        let quick_report: t2.IReportModelEx | null = null;
        let error: string | null = null;
        let insights: t.IEventDataEx[] | null = null;
        let extra_lines: t.IExtraLines[] | null = null;
        let filtered_orders: FilterLinesOrders[] = [];
        let translations: t.ITranslations | undefined = undefined;
        let cache_hash: CacheParams | undefined = undefined;
        let isClose = false;

        const unloadEventListener = () => {
            isClose = true;
        };
        window.addEventListener("beforeunload", unloadEventListener);
        try {
            // prepare lines we want to include in the quick simulation
            const line_uuid_set: Set<string> = new Set();
            const lines: rt.IRunQuickSimulationLine[] = [];

            lines_orders.forEach(line_order => {
                line_uuid_set.add(line_order.line_uuid);
                lines.push(ReportLogic.generateJobItem(line_order, config, true));
            });

            if (lines.length === 0) {
                return { quick_report, error, insights, extra_lines: null, filtered_orders };
            }

            // prepare unscheduled orders so we can accuractely estimate extra lines
            const unscheduled_lines: rt.IRunQuickSimulationLine[] = [];

            for (const unscheduled_line_orders of unscheduled_orders) {
                line_uuid_set.add(unscheduled_line_orders.line_uuid);
                unscheduled_lines.push(ReportLogic.generateJobItem(unscheduled_line_orders, config, false));
            }

            // prepare simulation parameters
            const current_shift = shiftNumber(new Date());
            const planning_shifts = PropertiesLogic.getNumShiftsFromDayTag(
                GanttChartSource.planning_table,
                line_group_uuid,
                project_parallel_orders
            );

            cache_hash = { ...ReportCache.getCacheHash(project_parallel_orders) };

            // if we are projecting parallel orders we are doing either initial or
            // final simulation that is used to generate plan export diff
            // we include split orders in the input for completeness
            const split_orders: rt.IRunQuickSimulationSplitOrder[] = [];
            if (project_parallel_orders) {
                const split_orders_map = new Map<string, rt.IRunQuickSimulationSplitOrder>();
                lines_orders.forEach(lo => {
                    for (const order of lo.orders) {
                        // check if chunk and if we have parent order
                        let parent_order: rt.IRunQuickSimulationSplitOrderParent | null = null;
                        if (LinesOrdersLogic.isChunkOrder(order)) {
                            if (!parent_order) {
                                const parent_order_uuid = order.parent_order_uuid;
                                if (parent_order_uuid) {
                                    const parent = LinesOrdersMaps.parents_of_chunks.get(parent_order_uuid);
                                    if (parent) {
                                        parent_order = ReportLogic.generateSplitOrderParent(parent);
                                    }
                                }
                            }
                        }
                        if (parent_order) {
                            const split_order = split_orders_map.get(parent_order.order_uuid);
                            if (split_order !== undefined) {
                                split_order.chunks.push(ReportLogic.generateSplitOrderChunk(order));
                            } else {
                                split_orders_map.set(parent_order.order_uuid, {
                                    parent: parent_order,
                                    chunks: [ReportLogic.generateSplitOrderChunk(order)],
                                });
                            }
                        }
                    }
                });
                split_orders.push(...split_orders_map.values());
            }

            const plant_tags = plant_uuid ? BusinessLogic.getPlantTags(plant_uuid) : {};

            const skip_tool_setup_after_inactivity = PLANT_TAGS_ACCESS.skip_tool_setup_after_inactivity(plant_tags);
            const batch_order_split_on_shifts = PLANT_TAGS_ACCESS.batch_order_split_on_shifts(plant_tags);
            const quick_simulation_req: rt.IRunQuickSimulationReq = {
                from_shift: {
                    shift: 0,
                    week: current_shift.week,
                    year: current_shift.year,
                },
                next_shift: {
                    shift: current_shift.shift,
                    week: current_shift.week,
                    year: current_shift.year,
                },
                predict_shifts: planning_shifts,
                predict_stock_shifts: planning_shifts,
                stock_plant_insights_num_shifts: planning_shifts,
                stock_shopfloor_insights_num_shifts: planning_shifts,
                lines: lines.map(line => ({ ...line, shifts: line.shifts.slice(0, planning_shifts) })),
                unscheduled_lines: unscheduled_lines.map(line => ({ ...line, shifts: [] })),
                tags: {
                    line_uuids: [...line_uuid_set].join(","),
                    plant_uuid: plant_uuid,
                    line_group_uuid: line_group_uuid,
                    stock_forecast: "true",
                    ...additional_tags,
                },
                project_parallel_orders: project_parallel_orders || false,
                cache_hash: cache_hash.cache_hash,
                cache_reset: cache_hash.cache_reset,
                strip_shifts: planning_shifts,
                skip_stock,
                split_orders,
                skip_tool_setup_after_inactivity,
                batch_order_split_on_shifts,
            };

            if (parameters.extra_lines && parameters.extra_lines.length > 0) {
                quick_simulation_req.extra_lines = parameters.extra_lines.map(el => ({ line: el.line }));
            }

            if (parameters.fixed_extra_lines && parameters.fixed_extra_lines.length > 0) {
                quick_simulation_req.fixed_extra_lines = parameters.fixed_extra_lines;
            }

            // check if we have a named simulation for what-if
            if (title != undefined) {
                quick_simulation_req.tags.title = title;
                quick_simulation_req.tags.what_if = "true";
                quick_simulation_req.tags.user_uuid = Auth.getLoggedinUser().uuid;
            }

            quick_simulation_req.tags.client_started_ts = Date.now() + "";

            if (ignore_orders_status != null) {
                const external_ignore_orders_status = new Map<string, boolean>(ignore_orders_status);
                for (const line of [...lines_orders, ...unscheduled_orders]) {
                    for (const order of line.orders) {
                        external_ignore_orders_status.delete(order.uuid);
                    }
                }

                quick_simulation_req.extra_line_ignore_orders = [...external_ignore_orders_status.entries()].map(
                    item => ({
                        uuid: item[0],
                        ignore: item[1],
                    })
                );
            }

            // execute the quick simulation
            const qr = await ReportLogic.loadReport(quick_simulation_req, parameters, full_report);
            if (additional_tags.simulation_type === SIMULATION_TYPES.full) {
                await getBackend2().manufacturing.recalculateInsights({});
            }
            quick_report = qr.quick_report;
            insights = qr.insights;
            error = qr.error;
            extra_lines = qr.extra_lines;
            filtered_orders = qr.filtered_orders;
            translations = qr.translations;
        } catch (err) {
            error = "quickSimulation: " + (err as Error).message;
            const client_side_error =
                `Quick sim error from line group ${parameters.config.line_group_uuid}: ` + (err as Error).message;
            if (!isClose) {
                await getBackend2().tracking.logClientsideError({
                    message: client_side_error,
                    module: "quick_simulation",
                    severity: (err as Error).message.indexOf("Failed to fetch") >= 0 ? "medium" : "high",
                });
            }
        }
        window.removeEventListener("beforeunload", unloadEventListener);

        return {
            quick_report,
            error,
            insights,
            extra_lines,
            filtered_orders,
            translations,
            cache_hash,
        };
    };

    static canceled_report_req: Map<string, boolean> = new Map();

    static showWarningMsg = () => {
        const e = new CustomEvent("error-handler-event", {
            detail: {
                msg: translate("common.simulation_return", "Simulation is taking longer than usual, please wait..."),
                onCancel: () => {},
            },
        });

        const el = document.querySelector("#error-handler");
        if (el) {
            el.dispatchEvent(e);
        }
    };

    static hideWarningMsg = () => {
        const e = new CustomEvent("error-handler-event", {
            detail: {
                msg: "",
                onCancel: () => {},
            },
        });

        const el = document.querySelector("#error-handler");
        if (el) {
            el.dispatchEvent(e);
        }
    };

    /** Utility method for waiting on job */
    static waitForReport = async (uuid: string, project_parallel_orders: boolean): Promise<string | Error | null> => {
        const CHECK_INTERVAL = 3 * TIME_RANGES.SECOND;
        const MAX_WAIT_TIME = 60 * TIME_RANGES.MINUTE;
        if (!project_parallel_orders) {
            ReportLogic.showWarningMsg();
        }
        return new Promise((resolve, reject) => {
            let counter = 0;
            const loopCall = async (): Promise<void> => {
                try {
                    const res = await getBackend2().reports.getReportStatus({ id: uuid });
                    if (!res) {
                        ReportLogic.canceled_report_req.set(uuid, true);
                        ReportLogic.hideWarningMsg();
                        reject(new Error("Job with given uuid not found"));
                        return;
                    }
                    if (res.status === JOB_STATUS.errored) {
                        ReportLogic.canceled_report_req.set(uuid, true);
                        ReportLogic.hideWarningMsg();
                        reject(new Error("Job failed with error: " + res.status_msg));
                        return;
                    }
                    if (res.status === JOB_STATUS.ended) {
                        ReportLogic.canceled_report_req.set(uuid, true);
                        ReportLogic.hideWarningMsg();
                        resolve(res.status);
                        return;
                    }

                    // check if we have been waiting for too long
                    counter++;
                    if (counter * CHECK_INTERVAL >= MAX_WAIT_TIME) {
                        ReportLogic.canceled_report_req.set(uuid, true);
                        ReportLogic.hideWarningMsg();
                        reject(new Error("Job time-out: " + uuid));
                        return;
                    }

                    if (ReportLogic.canceled_report_req.get(uuid) === true) {
                        return;
                    }
                    setTimeout(loopCall.bind(this), CHECK_INTERVAL);
                } catch (err) {
                    ReportLogic.canceled_report_req.set(uuid, true);
                    ReportLogic.hideWarningMsg();
                    console.error(err);
                    reject(err);
                }
            };
            setTimeout(loopCall.bind(this), CHECK_INTERVAL);
        });
    };

    static async getQuickSimulation(
        req_parameters: rt.IRunQuickSimulationReq,
        job_uuid: string
    ): Promise<rt.IGetReportRes | null> {
        const status = await ReportLogic.waitForReport(job_uuid, !!req_parameters.project_parallel_orders);
        if (status === JOB_STATUS.ended) {
            const line_uuids = req_parameters.lines.map(l => l.uuid);
            const report_res = await getBackend2().reports.getReport({
                id: job_uuid,
                line_uuids: line_uuids.join(","),
            });
            return report_res;
        }
        return null;
    }

    static cached_override_extra_lines: t.IExtraLines[] | null = null;

    static getInsightTypes = () => {
        return [
            INSIGHT_TYPES.tool_setup,
            INSIGHT_TYPES.tool_overlap,
            INSIGHT_TYPES.operation_no_input_material,
            INSIGHT_TYPES.operation_delay_downstream,
            INSIGHT_TYPES.operation_constraint_violated,
            INSIGHT_TYPES.freeze_order_move,
            INSIGHT_TYPES.partially_scheduled,
            INSIGHT_TYPES.conditional_capacity_violated,
            INSIGHT_TYPES.planned_date_diff,
        ]; // always fetch all insight_types
    };

    static loadReport = async (
        req_parameters: rt.IRunQuickSimulationReq,
        parameters: ReportParameters,
        full_report?: t2.IReportModelEx | null
    ): Promise<ReportRes> => {
        const { config } = parameters;
        const { lines_orders } = config;
        const days =
            PropertiesLogic.getNumShiftsFromDayTag(GanttChartSource.planning_table, config.line_group_uuid) /
            SHIFTS_PER_DAY;
        // get report
        const line_uuids = lines_orders.map(x => x.line_uuid);
        req_parameters.language_code = getLang();
        req_parameters.dont_filter_extra_lines = true;

        if (
            !req_parameters.project_parallel_orders &&
            req_parameters.cache_reset === false &&
            ReportLogic.cached_override_extra_lines
        ) {
            req_parameters.override_extra_lines = ReportLogic.cached_override_extra_lines;
        }
        const simulation_res = await getBackend2().reports.runQuickSimulationSync(
            req_parameters,
            buildQuickSimSyncRequestHeaders(parameters.quickSimContext)
        );
        let simulation_report: rt.IReportModel | null = null;
        let error = "";
        let extra_lines: rt.IExtraLines[] = [];
        if (simulation_res.was_timeout && simulation_res.job_uuid) {
            const new_simulation_res = await ReportLogic.getQuickSimulation(req_parameters, simulation_res.job_uuid);
            if (new_simulation_res) {
                simulation_report = new_simulation_res.report;
                if (simulation_report) {
                    const report_input_data = simulation_report.input.data;
                    const extra_line_uuids = report_input_data.extra_line_uuids;
                    extra_lines = report_input_data.lines.filter(l => extra_line_uuids.includes(l.line));
                }
            }
        } else {
            simulation_report = simulation_res.report;
            extra_lines = simulation_res.extra_lines;
        }

        if (!req_parameters.project_parallel_orders && req_parameters.cache_reset === true) {
            ReportLogic.cached_override_extra_lines = extra_lines;
        }

        if (!simulation_report) {
            return {
                quick_report: null,
                insights: null,
                extra_lines: null,
                filtered_orders: [],
                error,
            };
        }

        if (req_parameters.cache_hash && req_parameters.cache_reset === false && full_report) {
            // cache_reset === false represents drag and drop so the report is not full
            // we should merge full report and drag and drop report before updating state
            simulation_report = TypeAdapter.transformReportModelFromEx(
                ReportLogic.mergeReports(TypeAdapter.transformReportModelToEx(simulation_report), full_report)
            );
        }

        const raw_simulation = addMaterialColors(simulation_report);

        let extra_line_uuids: string[] = [];
        if (extra_lines) {
            extra_line_uuids = extra_lines.map(el => el.line);
        }

        const filtered_orders = getOrdersWithZeroDuration(simulation_report, extra_line_uuids);

        try {
            const line_tags = lines_orders.map(line => ({ uuid: line.line_uuid, tags: line.tags }));
            const data = await Promise.all([
                prepareGanttChart2({
                    simulation: raw_simulation,
                    line_tags,
                }),
                prepareGanttChartInsights(
                    raw_simulation,
                    ReportLogic.getInsightTypes(),
                    [...line_uuids, ...extra_line_uuids],
                    0, // past weeks
                    days // shown days
                ),
            ]);

            return {
                quick_report: data[0].report,
                insights: data[1],
                error: null,
                extra_lines: extra_lines || null,
                filtered_orders,
                translations: simulation_report.translations,
            };
        } catch (e) {
            return {
                quick_report: null,
                insights: null,
                error: (e as Error).message,
                extra_lines: extra_lines || null,
                filtered_orders,
            };
        }
    };

    static getWeekAndYear = (offset: number, week: number, year: number) => {
        const shift = shiftNumber(dateFromWeekAndShift(week + offset, year, 0));
        return { week: shift.week, year: shift.year };
    };

    static reportTimeout = null;

    static generateJobItemOrder = (x: IOrderProducedModelEx): rt.IRunQuickSimulationOrder => {
        const freeze_time_start = x.tags[ORDER_TAGS.freeze_time_start];
        const freeze_time_end = x.tags[ORDER_TAGS.freeze_time_end];
        const subline_index = x.tags[ORDER_TAGS.subline_index];
        const parent_order_uuid = x.parent_order_uuid || "";

        return {
            capacity_factor: x.override_capacity_factor || x.capacity_factor,
            earliest_end: new Date(x.earliest_end).toISOString(),
            earliest_start: new Date(x.earliest_start).toISOString(),
            external_id: x.external_id,
            external_operation_id: x.external_operation_id,
            order_type: x.order_type,
            sim_ignore_earliest_start: x.sim_ignore_earliest_start,
            production_version: x.tags[ORDER_TAGS.production_version] || "",
            quantity_total: x.quantity_total,
            time_start: x.time_start,
            uuid: x.uuid,
            freeze_time_start: isStringNumber(freeze_time_start) ? parseFloat(freeze_time_start) : undefined,
            freeze_time_end: isStringNumber(freeze_time_end) ? parseFloat(freeze_time_end) : undefined,
            insight_freeze_order: x.tags[ORDER_TAGS.insight_freeze_order] === "true",
            subline_index: subline_index != undefined ? parseFloat(subline_index) : undefined,
            parent_order_uuid,
        };
    };

    static generateSplitOrderParent = (order: t.IOrderProducedModel): rt.IRunQuickSimulationSplitOrderParent => {
        return {
            capacity_factor: order.override_capacity_factor || order.capacity_factor,
            line_uuid: order.line_uuid,
            material_title: order.material_title,
            material_uuid: order.material_uuid,
            measurement_unit: order.measurement_unit,
            operation_external_id: order.external_operation_id,
            order_external_id: order.external_id,
            order_type: order.order_type,
            order_uuid: order.uuid,
            process_num: order.process_num,
            quantity_total: order.quantity_total,
        };
    };

    static generateSplitOrderChunk = (order: IOrderProducedModelEx): rt.IRunQuickSimulationSplitOrderChunk => {
        return {
            order_uuid: order.uuid,
            order_external_id: order.external_id,
            quantity_total: order.quantity_total,
        };
    };

    // gnerate job input for bigger plan simulation
    static generateJobItem = (
        line_orders: LineOrders,
        config: QuickSimArgs,
        filter_skip_sim: boolean
    ): rt.IRunQuickSimulationLine => {
        // collect order overrides
        let orders_mapped: rt.IRunQuickSimulationOrder[] = [];

        if (line_orders.orders.length > 0) {
            const orders: IOrderProducedModelEx[] = filter_skip_sim
                ? ReportLogic.getEnabledItems(line_orders.orders)
                : line_orders.orders;
            orders.sort((a, b) => a.sequence_weight - b.sequence_weight);

            orders_mapped = orders.map(ReportLogic.generateJobItemOrder);
        }

        // collect shift overrides
        const extractShiftData = (x: t.IShiftLineRec) => {
            return {
                enabled: x.enabled,
                shift: x.shift_number,
                week: x.week,
                year: x.year,
            };
        };

        return {
            uuid: line_orders.line_uuid,
            orders: orders_mapped,
            shifts: line_orders.shifts.map(extractShiftData),
        };
    };

    static getLineUuidFromInsight = (insight: rt.IEventDataEx): string | null => {
        const line_uuid = insight.tags.line_uuid || insight.extra_data.line_uuid || null;
        return line_uuid;
    };

    static getLineUuidsFromInsight = (insight: rt.IEventDataEx): string[] | null => {
        return insight.tags.line_uuids ? insight.tags.line_uuids.split(",") : null;
    };

    static getOrderUuidFromInsight = (insight: rt.IEventDataEx): string => {
        const order_uuid = insight.tags.order_uuid || insight.extra_data.order_uuid;
        return order_uuid;
    };

    static getOrderUuidsFromInsight = (insight: rt.IEventDataEx): string[] | null => {
        return insight.tags.order_uuids ? insight.tags.order_uuids.split(",") : null;
    };

    static getOrderExternalIdFromInsight = (insight: rt.IEventDataEx): string => {
        const order_external_id = insight.tags.order_external_id || insight.extra_data.order_external_id;
        return order_external_id;
    };

    static insightsDiff = (
        original_insights: rt.IEventDataEx[],
        current_insights: rt.IEventDataEx[]
    ): InsightDiff[] => {
        const original_hash_map: Map<string, rt.IEventDataEx> = new Map();
        for (const original_insight of original_insights) {
            original_hash_map.set(original_insight.tags.hash || "", original_insight);
        }
        const current_hash_map: Map<string, rt.IEventDataEx> = new Map();
        for (const current_insight of current_insights) {
            current_hash_map.set(current_insight.tags.hash || "", current_insight);
        }

        const allowed_insights = [
            INSIGHT_TYPES.operation_delay_downstream,
            INSIGHT_TYPES.operation_no_input_material,
            INSIGHT_TYPES.operation_constraint_violated,
            INSIGHT_TYPES.freeze_order_move,
        ];
        // go over and find differences on insights
        const insight_diffs: InsightDiff[] = [];
        for (const original_insight of original_insights) {
            const original_hash = original_insight.tags.hash || "";
            // bad hash, nothing to compare
            if (original_hash === "" || !allowed_insights.includes(original_insight.type as InsightTypesEnum)) {
                continue;
            }
            // check if we have it in new set as well
            if (!current_hash_map.has(original_hash)) {
                // this insight no longer active, report success!
                insight_diffs.push({
                    diff_type: INSIGHT_DIFF_TYPE.solved,
                    original_insight,
                });
            }
        }
        for (const current_insight of current_insights) {
            const current_hash = current_insight.tags.hash || "";
            // bad hash, nothing to compare
            if (current_hash === "" || !allowed_insights.includes(current_insight.type as InsightTypesEnum)) {
                continue;
            }
            // check if we have it in new set as well
            if (!original_hash_map.has(current_hash)) {
                // this insight is new, report we created it
                insight_diffs.push({
                    diff_type: INSIGHT_DIFF_TYPE.new,
                    current_insight,
                });
            } else {
                // this isnight is both old and new
                insight_diffs.push({
                    diff_type: INSIGHT_DIFF_TYPE.same,
                    original_insight: original_hash_map.get(current_hash),
                    current_insight,
                });
            }
        }

        return insight_diffs;
    };

    static getVisibleLines = (report_data: t2.IReportModelEx): string[] => {
        const line_uuid_set: Set<string> = new Set();
        if (report_data.input.data.extra_line_uuids) {
            for (const line_uuid of report_data.input.data.extra_line_uuids) {
                line_uuid_set.add(line_uuid);
            }
        }
        if (report_data.input.data.lines) {
            for (const line of report_data.input.data.lines) {
                line_uuid_set.add(line.line);
            }
        }
        for (const line of report_data.result.line_shift_features) {
            line_uuid_set.add(line.line);
        }
        for (const line of report_data.result.orders) {
            line_uuid_set.add(line.line);
        }
        return [...line_uuid_set];
    };

    static getVisibleLinesForLineGroup = (report_data: t2.IReportModelEx, line_group_uuid: string | null): string[] => {
        if (!line_group_uuid) {
            return [];
        }
        const line_group = BusinessLogic.getLineGroupForUser(line_group_uuid);
        if (!line_group) {
            return [];
        }
        const group_line_uuids = new Set(line_group.line_uuids);
        return ReportLogic.getVisibleLines(report_data).filter(uuid => group_line_uuids.has(uuid));
    };

    static getLineStatuses = async (
        report_data: t2.IReportModelEx,
        line_group_uuid: string | null,
        dispatch: (arg: UpdateCurrentLineStatusesAction) => void
    ): Promise<void> => {
        const line_statuses_map = await ReportLogic.fetchLineStatusesMap(
            ReportLogic.getVisibleLinesForLineGroup(report_data, line_group_uuid)
        );
        dispatch({ type: UPDATE_CURRENT_LINE_STATUSES_ACTION_NAME, data: line_statuses_map });
    };

    static fetchLineStatusesMap = async (line_uuids: string[]): Promise<Map<string, t.ILineStatusItem | Error>> => {
        if (line_uuids.length === 0) return new Map();

        try {
            const { data } = await getBackend2().manufacturing.getLineStatuses({
                lineUuids: line_uuids,
                includeStale: true,
            });
            return new Map(data.map(item => [item.lineUuid, item]));
        } catch (error) {
            console.error("Error fetching line statuses: ", error);
            return new Map(line_uuids.map(lineUuid => [lineUuid, new Error(translate("common.error"))]));
        }
    };
}

export type LinesOrdersParameters = {
    line_group_uuid: string;
    year: number;
    week: number;
    shift_edit_weeks: number;
};

export type ExtraLinesOrdersParameters = LinesOrdersParameters & {
    extra_lines: string[];
};

type RearrangeOrdersValue = {
    line_uuid: string;
    earliest_start: number;
};

export type RearrangeOrdersMap = Map<string, RearrangeOrdersValue>;

export class LinesOrdersLogic {
    static converted_order_id_indicator = "W";

    static isChunkOrder = (order: IOrderProducedModelEx): boolean => {
        return (
            order.order_type === ORDER_TYPE.chunk &&
            order.generated_by === ORDER_GENERATED_BY.planning_board_split &&
            !!order.parent_order_uuid
        );
    };

    static mapLineOrderToChunk = (order: IOrderProducedModelEx, parent_order_uuid: string): ChunkedOrder => {
        return {
            uuid: order.uuid,
            external_id: order.external_id,
            process_num: order.process_num,
            quantity_produced: order.quantity_produced,
            quantity_total: order.quantity_total,
            line_uuid: order.line_uuid,
            parent_order_uuid,
        };
    };

    static isOrderReadOnly = (line: LineOrders, order: IOrderProducedModelEx) => {
        if (order.tags[ORDER_TAGS.linked_operation]) {
            return true;
        }
        const show_chunks = line.tags[LINE_TAGS.gantt_chunk_orders] === "true";
        if (show_chunks) {
            return !!order.parent_order_uuid;
        }
        return false;
    };

    static rearrangeOrdersMap = (lines_orders: LineOrders[], unscheduled_orders: LineOrders[]): RearrangeOrdersMap => {
        const rearrange_orders_map = new Map<string, RearrangeOrdersValue>();

        for (const line_orders of lines_orders) {
            for (const order of line_orders.orders) {
                rearrange_orders_map.set(order.uuid, {
                    line_uuid: line_orders.line_uuid,
                    earliest_start: order.earliest_start,
                });
            }
        }

        for (const unscheduled_line of unscheduled_orders) {
            for (const unscheduled_order of unscheduled_line.orders) {
                rearrange_orders_map.set(unscheduled_order.uuid, {
                    line_uuid: unscheduled_line.line_uuid,
                    earliest_start: unscheduled_order.earliest_start,
                });
            }
        }

        return rearrange_orders_map;
    };

    static getLineGroupForLine = (line_uuid: string, line_group_uuid: string): t.ILineGroupData | null => {
        // line_group_uuid attribute is the prefered line group in case the function returns multiple line groups
        const line_groups = BusinessLogic.getLineGroupsForLine(line_uuid, line_group_uuid);
        // a line can have multiple line groups on gantt we choose the first line group
        if (line_groups.length > 0) {
            return line_groups[0];
        }
        return null;
    };

    static isOverlaping = (line_orders: LineOrders): boolean => {
        return line_orders.tags[LINE_TAGS.overlapping_orders_line] === "true";
    };

    static findOrder = (lines_orders: LineOrders[], order_uuid: string): IOrderProducedModelEx | null => {
        for (const line of lines_orders) {
            const line_order: IOrderProducedModelEx | undefined = (line.orders as IOrderProducedModelEx[]).find(
                o => o.uuid === order_uuid
            );
            if (line_order) {
                return line_order;
            }
        }
        return null;
    };

    static findOrderIndexLinesOrders = (lines_orders: LineOrders[], order_uuid: string): number => {
        for (const line_order of lines_orders) {
            const has_order = line_order.orders.some(o => o.uuid === order_uuid);
            if (has_order) {
                return line_order.orders.findIndex(o => o.uuid === order_uuid);
            }
        }
        return -1;
    };

    static filterLinesOrdersByOrderUuids(in_lines: LineOrders[], order_uuids: string[]): LineOrders[] {
        const lines_orders: LineOrders[] = [];
        for (const line_orders of in_lines) {
            const orders: IOrderProducedModelEx[] = [];
            for (const order of line_orders.orders) {
                if (order_uuids.includes(order.uuid)) {
                    orders.push(order);
                }
            }
            if (orders.length > 0) {
                lines_orders.push({
                    ...line_orders,
                    orders: orders,
                });
            }
        }
        return lines_orders;
    }

    static filterLinesOrdersByOrderExternalIds(in_lines: LineOrders[], external_ids: string[]): LineOrders[] {
        const lines_orders: LineOrders[] = [];
        for (const line_orders of in_lines) {
            const orders: IOrderProducedModelEx[] = [];
            for (const order of line_orders.orders) {
                if (external_ids.includes(order.external_id)) {
                    orders.push(order);
                }
            }
            if (orders.length > 0) {
                lines_orders.push({
                    ...line_orders,
                    orders: orders,
                });
            }
        }
        return lines_orders;
    }

    static filterLines(line_group_uuid: string, lines: ILineData[]): ILineData[] {
        const automatic_scheduling = BusinessLogic.getLineGroupTagBool(
            line_group_uuid,
            LINE_GROUP_TAGS.automatic_scheduling,
            false
        );

        const res: ILineData[] = [];
        for (const line of lines) {
            // skip lines not part of planning
            if (line.tags[LINE_TAGS.skip_planning] === "true") {
                continue;
            }
            // we only plan bottlenecks, rest is automatic through simulation
            if (automatic_scheduling && line.tags[LINE_TAGS.bottleneck] !== "true") {
                continue;
            }
            // we can include the line in the planning board
            res.push(line);
        }
        return res;
    }

    static mapILineDataLinesOrders(lines: ILineData[]): Partial<LineOrders>[] {
        return lines.map((line: ILineData, i: number) => {
            return {
                line_title: line.title,
                line_uuid: line.uuid,
                line_index: i,
                line_external_id: line.external_id,
            };
        });
    }

    /**
     * Adds a `campaign_group` property to each order in the input array, based on the order's campaign key.
     */
    static addCampaignGroups = (
        orders: IOrderProducedModelEx[],
        reference_key: string | null,
        reference_only: boolean = false
    ): IOrderProducedModelEx[] => {
        const order_keys = orders.map(order => order.tags.campaign_key || "");
        if (order_keys.filter(Boolean).length === 0) {
            return orders;
        }

        let campaign_groups_ref: (CampaignGroup | undefined)[] = [];
        if (reference_key) {
            campaign_groups_ref = getCampaignGroupsReference(order_keys, reference_key);
        }

        const campaign_groups = reference_only
            ? campaign_groups_ref
            : // Mix values from both arrays together
              getAlternateCampaignGroups(order_keys).map((group, index) => campaign_groups_ref[index] ?? group);
        return orders.map((order, index) => {
            return {
                ...order,
                campaign_group: campaign_groups[index],
            };
        });
    };

    static loadMaterials = async (lines_orders: LineOrders[], dispatch: (args: LineOrdersLoadActions) => void) => {
        const material_external_ids: Set<string> = new Set();
        for (const line_orders of lines_orders) {
            for (const order of line_orders.orders) {
                material_external_ids.add(order.material_external_id);
            }
        }

        const res_materials = await getBackend2().manufacturing.searchMaterialsSimple({
            external_ids: [...material_external_ids],
        });
        dispatch({ type: UPDATE_MATERIALS, data: res_materials.data });
    };

    static load = async (
        parameters: LinesOrdersParameters,
        dispatch: (args: LineOrdersLoadActions | SetErrorAction | UpdateFixedExtraLinesAction) => void
    ) => {
        const { line_group_uuid, year, week, shift_edit_weeks } = parameters;
        const { uuid: orgUuid } = Auth.getOrgData();

        // Load wall time clock start (immediately before line_orders loading true).
        const planningTableLoadWallTimeStartedAt = performance.now();
        startPlanningTableLoadWallTimeSession({
            startedAt: planningTableLoadWallTimeStartedAt,
            orgUuid: orgUuid,
            lineGroupUuid: line_group_uuid,
        });

        dispatch({ type: SET_ERROR, data: null });
        dispatch({ type: LINE_ORDERS_LOADING_ACTION_NAME, data: true });

        // orders outside this interval will not be fetched
        const planning_horizon_days = BusinessLogic.getLineGroupTagInt(
            line_group_uuid,
            LINE_GROUP_TAGS.planning_table_horizon_days,
            21
        );

        const plant_uuid = local_settings.getItem(FIELD_LAST_PLANT);
        const plant_tags = plant_uuid ? BusinessLogic.getPlantTags(plant_uuid) : {};
        const ignore_horizon_work_orders = PLANT_TAGS_ACCESS.planning_table_ignore_horizon_work_orders(plant_tags);
        const res_lines = await getBackend2().manufacturing.getLines({ line_group_uuids: [line_group_uuid] });

        const line_group = res_lines.lines.length > 0 ? BusinessLogic.getLineGroupForUser(line_group_uuid) : null;

        if (line_group) {
            let custom_order_fields: CustomOrderField[] = [];
            if (line_group.tags.custom_order_fields) {
                try {
                    custom_order_fields = JSON.parse(line_group.tags.custom_order_fields);
                } catch (err) {
                    console.warn("Error parsing line_group.tags.custom_order_fields: ", err);
                }
            }

            const custom_column_ids = custom_order_fields.map(field => field.id);
            const custom_column_ids_waterfall = custom_order_fields
                .filter(field => field.views.includes("waterfall"))
                .map(field => field.id);

            if (custom_column_ids.length > 0) {
                const planning_table_columns = PlanningTableColumns.getInstance();

                planning_table_columns.addColumnIds(custom_column_ids, {
                    after_column_id: "material_title",
                    table_size: "big",
                });
                planning_table_columns.addColumnIds(custom_column_ids, {
                    after_column_id: "material",
                    table_size: "small",
                });
                planning_table_columns.addColumnIds(custom_column_ids_waterfall, {
                    after_column_id: "workorder",
                    table_size: "mini",
                });

                for (const field of custom_order_fields) {
                    planning_table_columns.addCustomColumnDefinition(field.id, {
                        translate: () => field.title,
                        type: field.type,
                        align: "right",
                    });
                }
            }
        }

        const lines_orders = LinesOrdersLogic.mapILineDataLinesOrders(
            LinesOrdersLogic.filterLines(line_group_uuid, res_lines.lines)
        );
        if (lines_orders.length === 0) {
            console.warn(`
                WARNING: Be aware that Lines are filtered out, because of skip_planning or bottleneck line tags or automatic_scheduling line group tag.
                This could cause an empty planning board.`);
        }

        let res_planning_data: t.IGetPlanningDataRes | null = null;
        try {
            res_planning_data = await getBackend2().manufacturing.getPlanningData({
                calculate_hash: false,
                line_uuids: lines_orders.map(x => x.line_uuid as string), // because of Partial
                planning_horizon_days,
                year,
                week_from: week,
                week_count: shift_edit_weeks,
                language_code: getLang(),
                is_planning_table: true,
                ignore_horizon_work_orders,
            });
        } catch (e) {
            dispatch({ type: SET_ERROR, data: (e as Error).message });
            if ((e as Error).message.includes("line group contains split operations for chunks")) {
                await getBackend2().tracking.logClientsideError({
                    message: `line group ${line_group_uuid} contains split operations for chunks`,
                    module: "quick_simulation",
                    severity: "high",
                });
            }
        }

        if (!res_planning_data) {
            cancelPlanningTableLoadWallTimeSession();
            return;
        }

        const unscheduled_lines: LineOrders[] = [];
        const ignored_lines: LineOrders[] = [];
        const past_orders: IOrderProducedModelEx[] = [];

        for (const line of lines_orders) {
            // find line in the server results
            const res_idx = res_planning_data.lines.findIndex(x => x.line_uuid === line.line_uuid);
            // if nothing from the server side, skip
            if (res_idx === -1) {
                continue;
            }
            // get server response for line
            const res: t.ILinePlanningData = res_planning_data.lines[res_idx];

            let orders = res.orders.sort((order_a, order_b) => order_a.sequence_weight - order_b.sequence_weight);

            const unscheduled_orders: t.IOrderProducedModel[] = [];

            orders = orders.filter(o => {
                if (o.skip_sim === true) {
                    unscheduled_orders.push(o);
                    return false;
                }
                return true;
            });
            const filtered_past_orders: IOrderProducedModelEx[] = orders.filter(
                o => o.earliest_start < new Date().getTime()
            );
            for (const order of filtered_past_orders) {
                order.line_title = line.line_title;
                past_orders.push(order);
            }

            line.line_hash = res.hash;
            line.orders = orders;

            line.shifts = res.shifts.sort((a, b) => {
                const diff = a.shift_date - b.shift_date;
                return diff !== 0 ? diff : a.shift_number - b.shift_number;
            });
            line.tags = res.tags;

            if (unscheduled_orders.length > 0) {
                const unscheduled_line: LineOrders = { ...line } as LineOrders;
                unscheduled_line.orders = unscheduled_orders.map(TypeAdapter.transformOrderProducedModelToEx);
                unscheduled_lines.push(unscheduled_line);
            }

            const ignored_orders: t.IOrderProducedModel[] = res_planning_data.ignored_orders.filter(
                o => o.line_uuid === line.line_uuid
            );
            if (ignored_orders.length > 0) {
                const ignored_line: LineOrders = { ...line } as LineOrders;
                ignored_line.orders = ignored_orders.map(TypeAdapter.transformOrderProducedModelToEx);
                ignored_lines.push(ignored_line);
            }
        }

        if (unscheduled_lines.length > 0) {
            dispatch({ type: SET_UNSCHEDULED_ORDERS_ACTION_NAME, data: unscheduled_lines });
        }

        if (ignored_lines.length > 0) {
            dispatch({ type: SET_IGNORED_ORDERS_ACTION_NAME, data: ignored_lines });
        }

        if (past_orders.length > 0) {
            dispatch({ type: SET_PAST_ORDERS_ALERTS, data: past_orders });
        }

        dispatch({ type: UPDATE_FIXED_EXTRA_LINES, data: res_planning_data.fixed_extra_lines_uuids });

        dispatch({ type: UPDATE_LINES_ORDERS_ACTION_NAME, data: lines_orders as LineOrders[] });

        dispatch({
            type: SET_CHUNKED_ORDERS,
            data: { split_orders: res_planning_data.split_orders },
        });

        await ValidLinesOrdersHelper.fillLinesAndOrdersMaps(
            lines_orders as LineOrders[],
            unscheduled_lines,
            ignored_lines
        );
        await LinesOrdersLogic.loadMaterials(
            [...lines_orders, ...unscheduled_lines, ...ignored_lines] as LineOrders[],
            dispatch
        );

        // End of load table wall time after loadMaterials, before loading false; no emit on early return above.
        await recordPlanningTableRumDurationVital({
            name: PLANNING_TABLE_RUM_DURATION_VITALS.loadTableWallTime,
            durationMs: performance.now() - planningTableLoadWallTimeStartedAt,
            context: {
                orgUuid,
                lineGroupUuid: line_group_uuid,
            },
        });

        dispatch({ type: LINE_ORDERS_LOADING_ACTION_NAME, data: false });
        store.dispatch({ type: SET_TRANSLATIONS_MATERIALS, data: res_planning_data.translations.materials });
    };

    static getStockRequirementStartDate = (date_required: number, start_date_ts: number) => {
        const date = new Date(date_required);
        date.setHours(0);
        const start_date = (date.getTime() - start_date_ts) / TIME_RANGES.HOUR;
        if (start_date < 0) {
            return 0;
        }
        return start_date;
    };

    static getStockRequirements = async (
        plant_uuid: string,
        line_uuids: string[],
        properties: PropertiesState,
        should_filter: boolean = false
    ): Promise<t.IStockRequirementsShifts[]> => {
        const line_group_uuid = properties.line_group_uuid;
        if (!line_group_uuid) {
            return [];
        }
        const days = PropertiesLogic.getNumShiftsFromDayTag(properties.source_type, line_group_uuid) / SHIFTS_PER_DAY;
        const current_time = new Date().getTime();
        const res = await getBackend2().manufacturing.getStockRequirementsShifts({
            plant_uuid,
            line_uuids,
            max_shift_start_time: current_time + days * TIME_RANGES.DAY,
        });

        if (!should_filter) {
            return res.stock_requirements;
        }

        const stock_requirements_set = new Set();
        const current_shift = properties.current_shift;
        const week = properties.week;
        const year = properties.year;
        if (!week || !year) {
            return [];
        }
        const start_date_ts = getDateOfISOWeekShift(current_shift, week, year);
        const filtered_stock_requirements: t.IStockRequirementsShifts[] = [];
        for (const sr of res.stock_requirements) {
            const start_date = LinesOrdersLogic.getStockRequirementStartDate(
                sr.shift_start_time,
                start_date_ts.getTime()
            );
            const key = start_date + "_" + sr.material_external_id;
            if (stock_requirements_set.has(key)) {
                continue;
            }
            stock_requirements_set.add(key);
            filtered_stock_requirements.push(sr);
        }
        return filtered_stock_requirements;
    };

    static getAllLinesForLineGroup = async (linegroup_uuid: string): Promise<t.IGetLinesRes> => {
        const res = await getBackend2().manufacturing.getLines({ line_group_uuids: [linegroup_uuid] });
        return res;
    };

    static addOrderToLinesOrders = (
        lines_orders: LineOrders[],
        order: IOrderProducedModelEx,
        index: number
    ): LineOrders[] => {
        const line = lines_orders.find(l => l.line_uuid === order.line_uuid);
        if (line) {
            line.orders.splice(index, 0, order);
            return lines_orders;
        }
        return lines_orders;
    };

    static hasUnsavedChunks = (): boolean => {
        return [...LinesOrdersMaps.chunk_orders.values()].some(x => ORDER_TAGS_ACCESS.split_chunk_index(x.tags) < 0);
    };

    static generateConvertedOrderId = (id: string): string => {
        return `${LinesOrdersLogic.converted_order_id_indicator}${id}`;
    };

    static getConvertedOperation = (uuid: string): IOrderProducedModelEx | t.IOrderModelBase | null => {
        const operation =
            LinesOrdersMaps.converted_operations.get(uuid) || LinesOrdersMaps.converted_extra_operations.get(uuid);
        if (operation !== undefined) {
            return operation;
        }

        return null;
    };

    static getConvertedOperationParent = (uuid: string): IOrderProducedModelEx | t.IOrderModelBase | null => {
        const operation = LinesOrdersLogic.getConvertedOperation(uuid);
        if (operation !== null && operation.parent_order_uuid != null) {
            const parent_order_uuid = operation.parent_order_uuid;
            return LinesOrdersLogic.getConvertedOperation(parent_order_uuid);
        }

        return null;
    };

    static getConvertedOperationParentId = (uuid: string): string => {
        const operation = LinesOrdersLogic.getConvertedOperation(uuid);
        if (operation !== null) {
            return operation.parent_order_uuid || "";
        }

        return "";
    };

    static isConvertedOperation = (uuid: string): boolean => {
        const operation = LinesOrdersLogic.getConvertedOperation(uuid);
        return operation !== null && !!operation.parent_order_uuid;
    };

    static isConvertedOperationParent = (uuid: string): boolean => {
        const operation = LinesOrdersLogic.getConvertedOperation(uuid);
        return operation !== null && !operation.parent_order_uuid;
    };

    static getConversionData = (operations: t.IOrderModelBase[]): ConvertOrderData => {
        const operation_uuids_set: Set<string> = new Set(operations.map(operation => operation.uuid));
        const line_operations_map = new Map(
            [...LinesOrdersMaps.all_orders_map.values()]
                .filter(operation => operation_uuids_set.has(operation.uuid))
                .map(operation => [operation.uuid, operation])
        );
        const convert_order_data: ConvertOrderData = {
            operations: [],
        };

        for (const operation of operations) {
            const convert_operation_data: ConvertOperationData = {
                parent: operation,
                quantity: operation.quantity_total,
            };

            const planning_operation_uuid = operation.uuid;
            const planning_operation = LinesOrdersLogic.getConvertedOperation(planning_operation_uuid);

            const production_operation_uuid = LinesOrdersLogic.generateConvertedOrderId(planning_operation_uuid);
            const production_operation = LinesOrdersLogic.getConvertedOperation(production_operation_uuid);

            if (planning_operation !== null && production_operation !== null) {
                // Operation was converted, revert to original values
                convert_operation_data.quantity = 0;
                convert_operation_data.parent.quantity_total =
                    planning_operation.quantity_total + production_operation.quantity_total;
            } else {
                // If quantity was changed on planning table, use updated value
                const line_operation = line_operations_map.get(operation.uuid);
                if (line_operation !== undefined) {
                    operation.quantity_total = line_operation.quantity_total;
                }

                convert_operation_data.quantity = operation.quantity_total;
            }

            convert_order_data.operations.push(convert_operation_data);
        }

        return convert_order_data;
    };

    static getOrderConversionData = async (operation_uuid: string): Promise<ConvertOrderData> => {
        let order_external_id = "";
        if (LinesOrdersLogic.isConvertedOperation(operation_uuid)) {
            const parent_operation = LinesOrdersLogic.getConvertedOperationParent(operation_uuid);
            order_external_id = parent_operation?.external_id ?? "";
        } else {
            const operation = LinesOrdersMaps.all_orders_map.get(operation_uuid);
            order_external_id = operation?.external_id ?? "";
        }

        let order_operations: t.IOrderModelBase[] = [];
        try {
            order_operations = (
                await getBackend2().manufacturing.getOrdersSimple({
                    order_external_id,
                    order_types: [ORDER_TYPE.plan],
                    status: ORDER_STATUS.open,
                })
            ).operations;
        } catch (error) {
            console.error("Error while retrieving data from server", error);
        }

        const convert_order_data: ConvertOrderData = {
            operations: [],
        };

        if (order_operations.length === 0) {
            return convert_order_data;
        }

        const line_operations_map = new Map(
            [...LinesOrdersMaps.all_orders_map.values()]
                .filter(operation => operation.external_id === order_external_id)
                .map(operation => [operation.uuid, operation])
        );

        for (const operation of order_operations) {
            const convert_operation_data: ConvertOperationData = {
                parent: operation,
                quantity: operation.quantity_total,
            };

            const planning_operation_uuid = operation.uuid;
            const planning_operation = LinesOrdersLogic.getConvertedOperation(planning_operation_uuid);

            const production_operation_uuid = LinesOrdersLogic.generateConvertedOrderId(planning_operation_uuid);
            const production_operation = LinesOrdersLogic.getConvertedOperation(production_operation_uuid);

            if (planning_operation !== null && production_operation !== null) {
                // Operation was converted, revert to original values
                convert_operation_data.quantity = 0;
                convert_operation_data.parent.quantity_total =
                    planning_operation.quantity_total + production_operation.quantity_total;
            } else {
                // If quantity was changed on planning table, use updated value
                const line_operation = line_operations_map.get(operation.uuid);
                if (line_operation !== undefined) {
                    operation.quantity_total = line_operation.quantity_total;
                }

                convert_operation_data.quantity = operation.quantity_total;
            }

            convert_order_data.operations.push(convert_operation_data);
        }

        return convert_order_data;
    };
}

type RecommendPlanDispatch =
    | SetRecommendPlanRunningAction
    | SetRecommendPlanLinePlans
    | UpdateLinesOrdersAction
    | SetReportTypeTitleAction
    | SetRecommendExplanationsAction
    | UpdateLinesOrdersPlanAction
    | UpdateRecommendPlanAction
    | SetUnscheduledOrdersAction;

type RecommendPlanActionsLoad =
    | RecommendPlanDispatch
    | SetErrorAction
    | SetRecommendPlanErrorAction
    | UpdateAllOrdersRecommendPlanAction
    | SetSkipStockReport
    | SetRecommendWarningsAction
    | SetRecommendPlanModalAction;

export class RecommendPlanLogic {
    static prepareLinegroupPlanningParams(parameters: RunRecommendPlanParameters): t.IRunLinegroupPlanningReq {
        const { state, line_group_uuid, line_uuids, order_quanity_produced_map, order_time_start_map } = parameters;

        // in case we are not optimizing whole line group, we need to send to the
        // backend current state of the plan for the line group as is on the board.
        // backend does it's own quick simulation, so we only need to send data
        // required by quick simulation for the orders on lines not currently covered
        const lines_orders: t.IRunLinegroupPlanningSimulationLineOrders[] = [];
        for (const line_orders of parameters.lines_orders) {
            // if planning line, just include what we want to schedule, else include all
            const include_orders =
                line_uuids.includes(line_orders.line_uuid) || line_uuids.length === 0
                    ? line_orders.orders.filter(lo => {
                          if (lo.order_type === ORDER_TYPE.plan) {
                              return parameters.state.recommend_param_include_plan_orders;
                          }
                          return true;
                      })
                    : line_orders.orders;

            lines_orders.push({
                uuid: line_orders.line_uuid,
                orders: include_orders.map(lo => ({
                    capacity_factor: lo.override_capacity_factor || lo.capacity_factor,
                    earliest_end: toISODateString(new Date(lo.earliest_end)),
                    earliest_start: toISODateString(new Date(lo.earliest_start)),
                    external_id: lo.external_id,
                    external_operation_id: lo.external_operation_id,
                    order_type: lo.order_type,
                    fix_line_uuid: lo.tags[ORDER_TAGS.fix_line],
                    production_version: lo.tags[ORDER_TAGS.production_version] || "",
                    quantity_produced: order_quanity_produced_map.get(lo.uuid),
                    quantity_total: lo.quantity_total,
                    sim_ignore_earliest_start: lo.sim_ignore_earliest_start,
                    uuid: lo.uuid,
                    min_start_time: order_time_start_map ? order_time_start_map.get(lo.uuid) : undefined,
                })),
                shifts: line_orders.shifts.map(s => ({
                    enabled: s.enabled,
                    shift: s.shift_number,
                    week: s.week,
                    year: s.year,
                    uuid: s.shift_uuid,
                })),
            });
        }

        // if we send back also unscheduled orders, collect them in an array
        const unscheduled_orders: t.IRunLinegroupPlanningSimulationOrder[] = [];
        if (parameters.state.recommend_param_include_unscheduled_orders) {
            for (const line_orders of parameters.unscheduled_orders) {
                for (const lo of line_orders.orders) {
                    // skip plan orders if not selected in settings
                    if (lo.order_type === ORDER_TYPE.plan && !parameters.state.recommend_param_include_plan_orders) {
                        continue;
                    }
                    // we are good, remember the order
                    unscheduled_orders.push({
                        capacity_factor: lo.override_capacity_factor || lo.capacity_factor,
                        earliest_end: toISODateString(new Date(lo.earliest_end)),
                        earliest_start: toISODateString(new Date(lo.earliest_start)),
                        external_id: lo.external_id,
                        external_operation_id: lo.external_operation_id,
                        order_type: lo.order_type,
                        production_version: lo.tags[ORDER_TAGS.production_version] || "",
                        quantity_produced: order_quanity_produced_map.get(lo.uuid),
                        quantity_total: lo.quantity_total,
                        sim_ignore_earliest_start: lo.sim_ignore_earliest_start,
                        uuid: lo.uuid,
                    });
                }
            }
        }

        // we want to use same cache as quick sim
        const cache_hash = ReportCache.getCacheHash(false).cache_hash;
        return {
            // criteria scores must be between 0.0 and 1.0, so we normalize accordingly
            criteria: {
                delays: state.recommend_criteria_delays / 5.0,
                intermediate_stock: state.recommend_criteria_intermediate_stock / 5.0,
                tool_changes: state.recommend_criteria_tool_changes / 5.0,
            },
            linegroup_uuid: line_group_uuid,
            params: {
                beam_size: state.recommend_param_beam_size,
                cache_hash,
                can_change_workorder_line: state.recommend_param_can_change_workorder_line,
                customer_priority: state.recommend_param_customer_priority,
                freeze_orders: parameters.freeze_orders,
                plan_after_frozen_orders: state.recommend_param_plan_after_frozen_orders,
                number_of_fixed_shifts: state.recommend_param_freeze_shifts,
                time_horizont_shifts: state.recommend_param_time_horizont_shifts,
                include_plan_orders: state.recommend_param_include_plan_orders,
                line_uuids: line_uuids.length > 0 ? line_uuids : undefined,
                lines_orders,
                unscheduled_orders,
                track_input_material: state.recommend_param_track_input_material,
                use_productions_of_unscheduled_operations:
                    state.recommend_param_use_productions_of_unscheduled_operations,
                use_requirements_from_unscheduled_operations:
                    state.recommend_param_use_requirements_from_unscheduled_operations,
                use_safety_stock: state.recommend_param_use_safety_stock,
                strategy: state.recommend_strategy,
                meta_optimization: state.recommend_param_meta_optimization,
                override_shifts: state.recommend_param_override_shifts,
                need_score_factor: state.recommend_param_need_score_factor,
                setup_code_change_factor: state.recommend_param_setup_code_change_factor,
                work_duration_factor: state.recommend_param_work_duration_factor,
                planning_table_horizon_days: state.recommend_planning_table_horizon_days,
            },
        };
    }

    static load = async (
        parameters: RunRecommendPlanParameters,
        dispatch: (args: RecommendPlanActionsLoad) => void
    ) => {
        dispatch({ type: SET_RECOMMEND_PLAN_ERROR, data: null });
        dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: true });

        const line_group_planning_params = RecommendPlanLogic.prepareLinegroupPlanningParams(parameters);

        try {
            // ask for recommendation
            const { plan_uuid } = await getBackend2().manufacturing.runLinegroupPlanning(line_group_planning_params);
            RecommendPlanLogic.getRecommendPlan(plan_uuid, parameters, dispatch);
        } catch (e) {
            console.log("Recommend plan error: ", (e as Error).message);
            dispatch({
                type: SET_RECOMMEND_PLAN_ERROR,
                data: (e as Error).message || AUTOMATIC_PLANNING_ERROR_FALLBACK,
            });
            dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
        }
    };

    static getAdditionalWarnings = (
        lines_orders: LineOrders[], // existing scheduled plan
        line_plans: IPlanLine[] // new plan, should be subset of lines in lines_orders
    ): IPlanWarning[] => {
        const warnings: IPlanWarning[] = [];

        const lines_orders_map: Map<string, LineOrders> = new Map();
        const order_lines_map: Map<string, Set<LineOrders>> = new Map();
        const orders_map: Map<string, IOrderProducedModelEx> = new Map();
        const order_line_map: Map<string, LineOrders> = new Map();
        for (const line_orders of lines_orders) {
            lines_orders_map.set(line_orders.line_uuid, line_orders);

            for (const order of line_orders.orders) {
                orders_map.set(order.uuid, order);
                order_line_map.set(order.uuid, line_orders);
                const order_lines = order_lines_map.get(order.external_id) || new Set();
                order_lines.add(line_orders);
                order_lines_map.set(order.external_id, order_lines);
            }
        }

        for (const line_plan of line_plans) {
            const line_orders = lines_orders_map.get(line_plan.uuid);
            if (line_orders === undefined) {
                continue;
            }

            const current_plan_ops_count_map: Map<string, number> = new Map();
            for (const order of line_orders.orders) {
                let count = current_plan_ops_count_map.get(order.uuid) || 0;
                count += 1;
                current_plan_ops_count_map.set(order.uuid, count);
            }

            const new_plan_ops_count_map: Map<string, number> = new Map();
            for (const planned_order of line_plan.operations) {
                let count = new_plan_ops_count_map.get(planned_order.uuid) || 0;
                count += 1;
                new_plan_ops_count_map.set(planned_order.uuid, count);
            }

            for (const [uuid, count] of current_plan_ops_count_map.entries()) {
                const new_count = new_plan_ops_count_map.get(uuid) || 0;
                if (new_count > count) {
                    const order = orders_map.get(uuid);
                    const line = order_line_map.get(uuid);
                    const order_lines = order !== undefined ? order_lines_map.get(order.external_id) : undefined;
                    if (order === undefined || line === undefined || order_lines === undefined) {
                        continue;
                    }

                    const shift_diffs: LineOrders[] = [];
                    for (const related_line of [...order_lines]) {
                        if (related_line.line_uuid === line.line_uuid) {
                            continue;
                        }

                        const shift_diff: LineOrders = {
                            ...related_line,
                            orders: [],
                            shifts: [],
                        };

                        for (let i = 0; i < related_line.shifts.length; i++) {
                            const related_shift = related_line.shifts[i];
                            const shift = line.shifts[i];
                            if (related_shift.enabled !== shift.enabled) {
                                shift_diff.shifts.push(related_shift);
                            }
                        }

                        if (shift_diff.shifts.length > 0) {
                            shift_diffs.push(shift_diff);
                        }
                    }

                    warnings.push({
                        operation_uuid: uuid,
                        type: PLANNING_WARNING_TYPE.none,
                        explanation: {
                            extra_data: {
                                split_operation: true,
                                line,
                                shift_diffs,
                            },
                            operation_uuid: uuid,
                            possible_prod_vers: [],
                            type: "",
                        },
                    });

                    break;
                }
            }
        }

        return warnings;
    };

    static rearrangeOrders = async (
        line_group_uuid: string,
        lines_orders: LineOrders[], // existing scheduled plan
        unscheduled_orders: LineOrders[], // existing unscheduled plan, should be subset of lines in lines_orders
        line_plans: IPlanLine[], // new plan, should be subset of lines in lines_orders
        rearrange_orders_map: RearrangeOrdersMap,
        planning_horizon_offset_msec: number,
        selected_line_uuids: string[]
    ): Promise<{
        lines_orders: LineOrders[]; // new scheduled plan
        unscheduled_orders: LineOrders[]; // new unscheduled plan
    }> => {
        console.log("rearrange_orders_map:", rearrange_orders_map);
        // assert on the inputs
        if (lines_orders.map(y => y.orders.map(x => x.skip_sim).some(z => z)).some(z => z)) {
            console.error("rearrangeOrders some operations in lines_orders have skip_sim true");
        }
        if (!unscheduled_orders.map(y => y.orders.map(x => x.skip_sim).every(z => z)).every(z => z)) {
            console.error("rearrangeOrders not all operations in unscheduled_orders have skip_sim true");
        }
        const input_line_order_uuids = deepClone(
            lines_orders.map(y => y.orders.map(x => x.uuid)).reduce((z, w) => z.concat(w), [])
        );
        const input_unscheduled_order_uuids = deepClone(
            unscheduled_orders.map(y => y.orders.map(x => x.uuid)).reduce((z, w) => z.concat(w), [])
        );
        if (input_line_order_uuids.length != [...new Set(input_line_order_uuids)].length) {
            console.error("rearrangeOrders lines_orders has duplicate uuids");
        }
        if (input_unscheduled_order_uuids.length != [...new Set(input_unscheduled_order_uuids)].length) {
            console.error("rearrangeOrders unscheduled_orders has duplicate uuids");
        }
        if (
            input_line_order_uuids.length + input_unscheduled_order_uuids.length !=
            [...new Set(input_line_order_uuids), ...new Set(input_unscheduled_order_uuids)].length
        ) {
            console.error("rearrangeOrders lines_orders and unscheduled_orders have common uuids");
        }

        // map of all orders, uuid => object
        const all_orders_map: Map<string, IOrderProducedModelEx> = new Map();
        // existing line for all orders
        const all_order_line_uuids_map: Map<string, string> = new Map();
        // map of line uuid to index in lines_orders array
        const line_uuid_index_map: Map<string, number> = new Map();

        // load scheduled orders
        for (const [line_index, line_orders] of lines_orders.entries()) {
            const line_uuid = line_orders.line_uuid;
            line_uuid_index_map.set(line_uuid, line_index);

            for (const order of line_orders.orders) {
                all_orders_map.set(order.uuid, order);
                all_order_line_uuids_map.set(order.uuid, line_uuid);
            }
        }
        // load unscheduled orders, we group them under fake line uuid
        const unscheduled_line_uuid = "UNSCHEDULED_LINE_UUID";
        for (const line_orders of unscheduled_orders) {
            for (const order of line_orders.orders) {
                all_orders_map.set(order.uuid, order);
                all_order_line_uuids_map.set(order.uuid, unscheduled_line_uuid);
            }
            // make sure we have this line in the line_orders array
            if (!line_uuid_index_map.has(line_orders.line_uuid)) {
                console.error(
                    `Line ${line_orders.line_uuid} included among unscheduled_orders but not in lines_orders.`
                );
            }
        }

        // placeholder for new plan
        const new_lines_orders: LineOrders[] = deepClone(lines_orders);
        // set of uuids of orders that were scheduled
        const done_order_uuids_set: Set<string> = new Set();

        // set of selected line uuids
        const selected_line_uuids_set = new Set(
            selected_line_uuids.length > 0 ? selected_line_uuids : line_uuid_index_map.keys()
        );
        // set of all line uuids that need to be updated with the new plan
        const todo_line_uuids_set = new Set(selected_line_uuids_set);

        // prepare new lines_orders from line_plans
        for (const line_plan of line_plans) {
            // shortcut
            const line_uuid = line_plan.uuid;

            // first add scheduled orders
            const line_orders: IOrderProducedModelEx[] = [];
            for (const planned_order of line_plan.operations) {
                if (!all_orders_map.has(planned_order.uuid)) {
                    console.error("Missing order", line_plan.title, planned_order);
                    continue;
                }
                // make a deep copy
                const new_order = deepClone(all_orders_map.get(planned_order.uuid)) as IOrderProducedModelEx;
                // for planned orders we never ignore earliest start
                new_order.sim_ignore_earliest_start = false;
                // assign planned timestamp to the order
                new_order.earliest_start = planned_order.start_timestamp;
                new_order.time_start = planned_order.start_timestamp;
                // everything scheduled is not skip sim
                new_order.skip_sim = false;
                // if we change the line we need to change production version
                const old_order_line_uuid = all_order_line_uuids_map.get(planned_order.uuid) || "?";
                if (line_uuid !== old_order_line_uuid) {
                    // report the move
                    console.log(
                        `Moving order ${new_order.external_id}/${new_order.order_type}  from ${old_order_line_uuid} to ${line_plan.uuid}`
                    );
                    // get new production version
                    if (planned_order.prod_ver && planned_order.prod_ver.length > 0) {
                        console.log(
                            `Changign production version ${new_order.tags[ORDER_TAGS.production_version]} => ${planned_order.prod_ver}`
                        );
                        new_order.tags[ORDER_TAGS.production_version] = planned_order.prod_ver;
                    }
                } else {
                    // if no line move, report none-the-less
                    console.log(
                        `Order ${new_order.external_id}/${new_order.order_type} kept on line ${line_plan.title}`
                    );
                }
                // add newly modified order to the new plan
                line_orders.push(new_order);
                // mark order as planned
                done_order_uuids_set.add(planned_order.uuid);
            }
            // find position of the line and update the orders array
            const line_index = line_uuid_index_map.get(line_uuid);
            // make sure we found something
            if (line_index !== undefined) {
                // assing orders to line
                new_lines_orders[line_index].orders = [...line_orders];
                // mark line as done
                todo_line_uuids_set.delete(line_uuid);
            } else {
                // we do not have this line in lines_orders, write error!
                console.error(`Missing line ${line_uuid} in lines_orders`);
            }
        }

        // clear orders on any line not included in line_plans
        for (const line_uuid of todo_line_uuids_set) {
            const line_index = line_uuid_index_map.get(line_uuid);
            if (line_index !== undefined) {
                // clear orders list on this line
                new_lines_orders[line_index].orders = [];
            } else {
                // should not happen since we fill todo lines from lines orders
            }
        }

        // go over orders and collect unscheduled
        const unscheduled_order_uuid_set = new Set<string>();
        for (const line_orders of lines_orders) {
            if (!selected_line_uuids_set.has(line_orders.line_uuid)) {
                continue;
            }

            // check if we have any existing scheduled orders that should go to unscheduled
            for (const line_order of line_orders.orders) {
                if (!done_order_uuids_set.has(line_order.uuid)) {
                    unscheduled_order_uuid_set.add(line_order.uuid);
                }
            }
        }
        const previously_unscheduled_uuid_set = new Set<string>();
        for (const unscheduled_line of unscheduled_orders) {
            for (const unscheduled_order of unscheduled_line.orders) {
                if (!done_order_uuids_set.has(unscheduled_order.uuid)) {
                    unscheduled_order_uuid_set.add(unscheduled_order.uuid);
                    // we keep track of orders that were beforee unscheduled
                    // just so we can report corretly to the console.log
                    previously_unscheduled_uuid_set.add(unscheduled_order.uuid);
                }
            }
        }

        const new_unscheduled_orders: LineOrders[] = lines_orders.map(line_orders => ({
            ...line_orders,
            orders: [],
        }));

        // go over all unscheduled orders and reset them to their original line
        // and make sure that earliest start is after planning horizon
        const lineGroup = BusinessLogic.getLineGroupForUser(line_group_uuid);
        const shouldMoveUnscheduledOperations = LINE_GROUP_TAGS_ACCESS.planning_table_unscheduled_move(lineGroup?.tags);
        const planningHorizon = new Date(Date.now() + planning_horizon_offset_msec);
        planningHorizon.setHours(0, 0, 0, 0);
        for (const unscheduled_order_uuid of unscheduled_order_uuid_set) {
            // make copy of the order
            const new_order = deepClone(all_orders_map.get(unscheduled_order_uuid)) as IOrderProducedModelEx;
            // mark it not used in simulation
            new_order.skip_sim = true;
            // get early data for the order
            const order_init_state = rearrange_orders_map.get(new_order.uuid);
            // find line to which we should place the order
            const line_uuid = order_init_state ? order_init_state.line_uuid : "";
            const line_index = line_uuid_index_map.get(line_uuid);
            // make sure we have this line
            if (line_index === undefined || line_index === -1) {
                console.error(
                    `Order ${new_order.external_id}/${new_order.uuid}/${line_uuid} not included in init state data`
                );
                continue;
            }

            if (shouldMoveUnscheduledOperations) {
                // update earliest start so it doesn't start before planning horizon
                const earliestStartTime = order_init_state ? order_init_state.earliest_start : 0;
                const startTime = Math.max(planningHorizon.getTime(), earliestStartTime);
                const duration = Math.abs(new_order.earliest_end - new_order.earliest_start);
                new_order.earliest_start = startTime;
                new_order.earliest_end = new_order.earliest_start + duration;
                new_order.time_start = startTime;
            }

            // we found order not yet scheduled for the line
            new_unscheduled_orders[line_index].orders.push(new_order);
            // marked as taken care of
            done_order_uuids_set.add(new_order.uuid);
            // report
            if (previously_unscheduled_uuid_set.has(unscheduled_order_uuid)) {
                console.log(`Order ${new_order.external_id}/${new_order.order_type} kept on ${unscheduled_line_uuid}`);
            } else if (shouldMoveUnscheduledOperations) {
                console.log(
                    `Order ${new_order.external_id}/${new_order.order_type} moved to ${unscheduled_line_uuid} with offset ${new Date(new_order.time_start!).toISOString()}`
                );
            } else {
                console.log(`Order ${new_order.external_id}/${new_order.order_type} moved to ${unscheduled_line_uuid}`);
            }
        }

        // check if any order missed on selected lines
        for (const order of all_orders_map.values()) {
            const line_uuid = all_order_line_uuids_map.get(order.uuid);
            if (line_uuid && selected_line_uuids_set.has(line_uuid) && !done_order_uuids_set.has(order.uuid)) {
                console.error(`Order ${order.external_id}/${order.uuid} not part of new plan!`);
            }
        }

        // Assert outputs
        // if some line of scheduled ops has some skip_sim=true, this is a problem
        if (new_lines_orders.map(y => y.orders.map(x => x.skip_sim).some(z => z)).some(z => z)) {
            console.error("rearrangeOrders some operations in new_lines_orders have skip_sim true");
        }
        // if not all unscheduled lines have all skip_sim=true, this is a problem
        if (!new_unscheduled_orders.map(y => y.orders.map(x => x.skip_sim).every(z => z)).every(z => z)) {
            console.error("rearrangeOrders not all operations in new_unscheduled_orders have skip_sim true");
        }
        const output_line_order_uuids = deepClone(
            new_lines_orders.map(x => x.orders.map(x => x.uuid)).reduce((z, w) => z.concat(w), [])
        );
        const output_unscheduled_order_uuids = deepClone(
            new_unscheduled_orders.map(x => x.orders.map(x => x.uuid)).reduce((z, w) => z.concat(w), [])
        );
        if (output_line_order_uuids.length != [...new Set(output_line_order_uuids)].length) {
            console.error("rearrangeOrders result_lines_orders has duplicate uuids");
        }
        if (output_unscheduled_order_uuids.length != [...new Set(output_unscheduled_order_uuids)].length) {
            console.error("rearrangeOrders new_unscheduled_orders has duplicate uuids");
        }
        if (
            output_line_order_uuids.length + output_unscheduled_order_uuids.length !=
            [...new Set(output_line_order_uuids), ...new Set(output_unscheduled_order_uuids)].length
        ) {
            console.error("rearrangeOrders result_lines_orders and new_unscheduled_orders have common uuids");
        }
        if (
            input_line_order_uuids.length + input_unscheduled_order_uuids.length !=
            output_line_order_uuids.length + output_unscheduled_order_uuids.length
        ) {
            console.error("rearrangeOrders input and output operation uuids sets missmatched");
            // Look for what's missing in the output
            const output_line_order_uuid_set = new Set(output_line_order_uuids);
            const output_unscheduled_order_uuid_set = new Set(output_unscheduled_order_uuids);
            for (const uuid of input_line_order_uuids) {
                if (!output_line_order_uuid_set.has(uuid) && !output_unscheduled_order_uuid_set.has(uuid)) {
                    const order = all_orders_map.get(uuid);
                    if (order != undefined) {
                        console.log(
                            `rearrangeOrders Scheduled input order ${order.external_id}/${order.order_type} is missing in output`
                        );
                    }
                }
            }
            for (const uuid of input_unscheduled_order_uuids) {
                if (!output_line_order_uuid_set.has(uuid) && !output_unscheduled_order_uuid_set.has(uuid)) {
                    const order = all_orders_map.get(uuid);
                    if (order != undefined) {
                        console.log(
                            `rearrangeOrders Unscheduled input order ${order.external_id}/${order.order_type} is missing in output`
                        );
                    }
                }
            }
        }
        console.log(
            `rearrangeOrders INPUT: sched (${input_line_order_uuids.length}) + ` +
                `unsched (${input_unscheduled_order_uuids.length}) = ` +
                (input_line_order_uuids.length + input_unscheduled_order_uuids.length)
        );
        console.log(
            `rearrangeOrders OUTPUT: sched (${output_line_order_uuids.length}) + ` +
                `unsched (${output_unscheduled_order_uuids.length}) = ` +
                (output_line_order_uuids.length + output_unscheduled_order_uuids.length)
        );

        const input_line_order_uuid_set = new Set(input_line_order_uuids);
        const output_line_order_uuid_set = new Set(output_line_order_uuids);
        const new_order_uuids = output_line_order_uuids.filter(uuid => !input_line_order_uuid_set.has(uuid));
        if (new_order_uuids.length > 0) {
            console.log({ new_order_uuids });
        }
        const missing_order_uuids = input_line_order_uuids.filter(uuid => !output_line_order_uuid_set.has(uuid));
        if (missing_order_uuids.length > 0) {
            console.log({ missing_order_uuids });
        }

        return {
            lines_orders: new_lines_orders,
            unscheduled_orders: new_unscheduled_orders,
        };
    };

    static disableShifts(lines_orders: LineOrders[], line_plans: IPlanLine[], schedule: t.IPlanSchedule[]) {
        const line_shift_map: Map<string, Map<string, t.IShiftLineRec>> = new Map();
        for (const { line_uuid, shifts } of lines_orders) {
            const shift_map: Map<string, t.IShiftLineRec> = new Map();
            for (const shift of shifts) {
                shift_map.set(shift.shift_uuid, shift);
            }

            line_shift_map.set(line_uuid, shift_map);
        }

        const line_uuids = line_plans.map(line_plan => line_plan.uuid);
        for (const schedule_shift of schedule) {
            const enable_line_uuids = schedule_shift.resources
                .filter(resource => resource.type === PLANNING_RESOURCE_TYPES.line)
                .map(resource => resource.uuid);
            for (const line_uuid of line_uuids) {
                const enabled = enable_line_uuids.includes(line_uuid);
                const shift_map = line_shift_map.get(line_uuid);
                if (shift_map !== undefined) {
                    const shift = shift_map.get(schedule_shift.uuid);
                    if (shift !== undefined) {
                        shift.enabled = enabled;
                    }
                }
            }
        }
    }

    static recommendPlanTimeout?: ReturnType<typeof setTimeout>;

    static async getRecommendPlan(
        plan_uuid: string,
        parameters: RunRecommendPlanParameters,
        dispatch: (args: RecommendPlanActionsLoad) => void
    ) {
        try {
            const backend = getBackend2().manufacturing;
            const { status } = await backend.getPlanStatus({ plan_uuid });
            if (status.status === JOB_STATUS.ended) {
                const { line_plans, warnings, explanations, schedule, score, stock_report_uuid } =
                    await backend.getPlanResult({
                        plan_uuid,
                        delete_plan: false,
                    });
                const recommend_plan_state = {
                    show_recommend_param_dialog: warnings.length > 0,
                    recommend_plan_params: parameters.state,
                    recommend_running: false,
                    error: null,
                    warnings: [
                        ...warnings,
                        ...RecommendPlanLogic.getAdditionalWarnings(parameters.lines_orders, line_plans),
                    ],
                    line_plans,
                    explanations,
                };
                dispatch({ type: SET_RECOMMEND_PLAN_LINE_PLANS, data: recommend_plan_state });

                const uscheduled_offset_timestamp =
                    parameters.state.recommend_param_unscheduled_offset_shifts *
                        SHIFT_CONSTS.SHIFT_DURATION_HOURS *
                        TIME_RANGES.HOUR +
                    TIME_RANGES.DAY; // offset by one day just in case

                const { lines_orders, unscheduled_orders } = await RecommendPlanLogic.rearrangeOrders(
                    parameters.line_group_uuid,
                    parameters.lines_orders,
                    parameters.unscheduled_orders,
                    line_plans,
                    parameters.rearrange_orders_map,
                    uscheduled_offset_timestamp,
                    parameters.line_uuids
                );

                if (
                    [PLANNING_META_OPTIMIZATION.stock, PLANNING_META_OPTIMIZATION.max_resources].includes(
                        parameters.state.recommend_param_meta_optimization as PlanningMetaOptimizationEnum
                    ) &&
                    schedule.length > 0
                ) {
                    RecommendPlanLogic.disableShifts(lines_orders, line_plans, schedule);
                }

                dispatch({
                    type: SET_REPORT_TYPE_TITLE_ACTION_NAME,
                    data: {
                        report_version_type: "version",
                        report_version_title: translate("Manufacturing.Planning.recommendation", "Recommendation"),
                    },
                });

                dispatch({ type: SKIP_STOCK_REPORT, data: false });

                dispatch({
                    type: UPDATE_ALL_ORDERS_RECOMMEND_PLAN,
                    data: {
                        unscheduled_orders,
                        lines_orders,
                        plan_uuid,
                        selected_line_uuids: parameters.line_uuids,
                    },
                });

                dispatch({
                    type: UPDATE_RECOMMEND_PLAN,
                    data: {
                        plan_uuid,
                        parameters,
                        plan_created_ts: new Date().getTime(),
                        score,
                        stock_report_uuid,
                    },
                });
            } else if (status.status === JOB_STATUS.errored) {
                console.log("Recommend plan errored: " + status.status_msg);
                dispatch({ type: SET_RECOMMEND_PLAN_ERROR, data: status.status_msg });
                dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
                dispatch({ type: SET_RECOMMEND_WARNINGS, data: [] });
            } else {
                clearTimeout(RecommendPlanLogic.recommendPlanTimeout);
                RecommendPlanLogic.recommendPlanTimeout = setTimeout(() => {
                    RecommendPlanLogic.getRecommendPlan(plan_uuid, parameters, dispatch);
                }, 250);
            }
        } catch (error) {
            console.log("Recommend plan error: ", (error as Error).message);
            clearTimeout(RecommendPlanLogic.recommendPlanTimeout);
            dispatch({
                type: SET_RECOMMEND_PLAN_ERROR,
                data: (error as Error).message || AUTOMATIC_PLANNING_ERROR_FALLBACK,
            });
            dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
            dispatch({ type: SET_RECOMMEND_WARNINGS, data: [] });
        }
    }

    static async getPlanScore(parameters: RunRecommendPlanParameters): Promise<{
        plan_result: t.IGetPlanResultRes | null;
        plan_uuid: string;
    }> {
        const wait_interval = 500;
        const max_wait_time = TIME_RANGES.HOUR;
        let wait_counter = 0;
        const backend = getBackend2().manufacturing;
        const waitForJob = async (plan_uuid: string): Promise<t.IGetPlanResultRes | null> => {
            try {
                const { status } = await backend.getPlanStatus({ plan_uuid });
                if (status.status === JOB_STATUS.ended) {
                    const res = await backend.getPlanResult({ plan_uuid, delete_plan: false });
                    return res;
                } else if (status.status === JOB_STATUS.errored) {
                    return null;
                } else {
                    if (wait_counter * wait_interval > max_wait_time) {
                        console.error(`getPlanScore time-out (plan_uuid: ${plan_uuid})`);
                        return null;
                    }

                    wait_counter++;
                    await new Promise(resolve => setTimeout(resolve, wait_interval));
                    return await waitForJob(plan_uuid);
                }
            } catch (error) {
                console.error(`getPlanScore error (plan_uuid: ${plan_uuid}): `, (error as Error).message);
                return null;
            }
        };

        const params_freeze_oreders: t.IRunLinegroupPlanningFreezeOrder[] = [];
        const freeze_orders_set: Set<string> = new Set();
        for (const line_orders of parameters.lines_orders) {
            for (const order of line_orders.orders) {
                if (freeze_orders_set.has(order.uuid)) {
                    continue;
                }

                params_freeze_oreders.push({
                    end_time: order.earliest_end,
                    start_time: order.earliest_start,
                    uuid: order.uuid,
                    line_uuid: line_orders.line_uuid,
                    failed_to_freeze: false,
                });
            }
        }

        const linegroup_planning_params = RecommendPlanLogic.prepareLinegroupPlanningParams({
            ...parameters,
            freeze_orders: params_freeze_oreders,
            state: {
                ...parameters.state,
                recommend_param_beam_size: 1,
            },
        });

        try {
            const { plan_uuid } = await backend.runLinegroupPlanning(linegroup_planning_params);
            const plan_result = await waitForJob(plan_uuid);
            return { plan_result, plan_uuid };
        } catch (error) {
            console.error(`getPlanScore error: `, (error as Error).message);
            return { plan_result: null, plan_uuid: "" };
        }
    }

    static getExplanationText = (
        explanation: t.IPlanExplanation & {
            extra_data: ExtraData;
        }
    ): string | null => {
        // if no automatic planning yet, no explanation yet
        if (explanation.type === PLANNING_EXPLANATION_TYPE.no_automatic_planning) {
            return null;
        }

        let text = translate(`Manufacturing.Planning.Explanation.${explanation.type}`, explanation.type);
        if (
            explanation.type === PLANNING_EXPLANATION_TYPE.scheduled &&
            explanation.extra_data.offset_material_external_id
        ) {
            text += ` ${translate("Manufacturing.Planning.Explanation.offset_material", "Material responsible for the time offset:")}`;
            text += ` ${explanation.extra_data.offset_material_title} (${explanation.extra_data.offset_material_external_id}).`;
        } else if (
            explanation.type === PLANNING_EXPLANATION_TYPE.scheduled &&
            explanation.extra_data.offset_previous_operation === true
        ) {
            text += ` ${translate("Manufacturing.Planning.Explanation.offset_operation", "Time offset due to previous operation.")}`;
        } else if (
            explanation.type === PLANNING_EXPLANATION_TYPE.missing_input_material &&
            explanation.extra_data.material_external_id
        ) {
            text += ` ${explanation.extra_data.material_title} (${explanation.extra_data.material_external_id}).`;
        }

        return text;
    };
}

export class SequencingPlanLogic {
    static run = async (parameters: RunBackwardPlanParameters, dispatch: (args: RecommendPlanActionsLoad) => void) => {
        dispatch({ type: SET_RECOMMEND_PLAN_ERROR, data: null });
        dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: true });

        // combine lines_orders and unscheduled_orders with report_data
        // (update start_date and end_date and add duration)
        const { report_data, lines_orders, unscheduled_orders } = parameters;
        if (!report_data) throw new Error("report_data is null");

        const next_shift_date = dateFromWeekAndShift(
            report_data.result.next_shift_time.week,
            report_data.result.next_shift_time.year,
            report_data.result.next_shift_time.shift_number
        );

        // Handle overriding values from report for scheduled orders
        const report_orders_map = BackwardPlanLogic.buildReportOrdersMap(report_data.result.orders);

        // Handle overriding values from report to orders (estimated_start, estimated_completion, ...)
        BackwardPlanLogic.processOrders(lines_orders, report_orders_map, next_shift_date);

        // Handle overriding values for unscheduled orders
        const report_unscheduled_orders_map = BackwardPlanLogic.buildUnscheduledOrdersMap(
            report_data.result.unscheduled
        );
        BackwardPlanLogic.processUnscheduledOrders(unscheduled_orders, report_unscheduled_orders_map);

        // Transform valid_lines_orders map to object
        const valid_lines_orders: t.IValidLinesOrders = ValidLinesOrdersHelper.orders_line_uuids_map
            ? BackwardPlanLogic.mapToObject(ValidLinesOrdersHelper.orders_line_uuids_map)
            : {};

        const selected_line_uuids_set = new Set(parameters.line_uuids);
        const selected_lines_orders =
            selected_line_uuids_set.size > 0
                ? lines_orders.filter(line_order => selected_line_uuids_set.has(line_order.line_uuid))
                : lines_orders;

        // Derive consider_alternative_lines from alternatives_strategy
        const alternatives_strategy =
            parameters.state.recommend_param_alternatives_strategy ?? "all-lines-keep-prefered";

        const req: t.IRunLinegroupSequencingPlanningReq = {
            line_orders: BackwardPlanLogic.transformLineOrders(selected_lines_orders),
            unscheduled_orders: BackwardPlanLogic.transformLineOrders(unscheduled_orders),
            params: {
                beam_width: 3,
                ignore_earliest_start: false,
                max_order_end_date: parameters.state.recommend_param_max_order_end_date,
                horizon_limit: parameters.state.recommend_param_horizon_limit,
                alternatives_strategy,
                include_history: parameters.state.recommend_param_include_history,
                consider_optimum_batch: parameters.state.recommend_param_consider_optimum_batch,
                run_until_phase: parameters.state.recommend_param_run_until_phase,
            } as t.IRunLinegroupSequencingPlanningParams,
            linegroup_uuid: parameters.line_group_uuid,
            valid_lines_orders,
        };
        const is_agrana = Auth.getOrgData().code == "agrana";
        if (!is_agrana) {
            req.params.ignore_earliest_start = true;
        }

        try {
            const { job_token } = await getBackend2().manufacturing.runLinegroupSequencingPlanning(req);
            BackwardPlanLogic.getPlan(job_token, parameters, next_shift_date.getTime(), dispatch);
        } catch (e) {
            dispatch({
                type: SET_RECOMMEND_PLAN_ERROR,
                data: (e as Error).message || AUTOMATIC_PLANNING_ERROR_FALLBACK,
            });
            dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
        }
    };
}

export class BackwardPlanLogic {
    static run = async (parameters: RunBackwardPlanParameters, dispatch: (args: RecommendPlanActionsLoad) => void) => {
        dispatch({ type: SET_RECOMMEND_PLAN_ERROR, data: null });
        dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: true });

        // combine lines_orders and unscheduled_orders with report_data
        // (update start_date and end_date and add duration)
        const { report_data, lines_orders, unscheduled_orders } = parameters;
        if (!report_data) throw new Error("report_data is null");

        const next_shift_date = dateFromWeekAndShift(
            report_data.result.next_shift_time.week,
            report_data.result.next_shift_time.year,
            report_data.result.next_shift_time.shift_number
        );

        // Handle overriding values from report for scheduled orders
        const report_orders_map = this.buildReportOrdersMap(report_data.result.orders);

        // Handle overriding values from report to orders (estimated_start, estimated_completion, ...)
        this.processOrders(lines_orders, report_orders_map, next_shift_date);

        // Handle overriding values for unscheduled orders
        const report_unscheduled_orders_map = this.buildUnscheduledOrdersMap(report_data.result.unscheduled);
        this.processUnscheduledOrders(unscheduled_orders, report_unscheduled_orders_map);

        // Transform valid_lines_orders map to object
        const valid_lines_orders: t.IValidLinesOrders = ValidLinesOrdersHelper.orders_line_uuids_auto_planning_map
            ? this.mapToObject(ValidLinesOrdersHelper.orders_line_uuids_auto_planning_map)
            : {};

        const backward_planning_input: t.IRunLinegroupBackwardPlanningReq = {
            line_orders: this.transformLineOrders(lines_orders), //  parameters.lines_orders,
            unscheduled_orders: this.transformLineOrders(unscheduled_orders), // parameters.unscheduled_orders,
            params: {
                consider_alternative_lines: parameters.state.recommend_param_consider_alternative_lines || false,
                consider_priority: parameters.state.recommend_param_consider_priority || false,
                consider_people_presences: parameters.state.recommend_param_consider_people_presences || false,
                consider_people_competences: parameters.state.recommend_param_consider_people_competences || false,
                start_date: next_shift_date.getTime(),
                planning_horizon:
                    parameters.state.recommend_param_time_horizont_shifts *
                    SHIFT_CONSTS.SHIFT_DURATION_HOURS *
                    TIME_RANGES.HOUR,
            },
            linegroup_uuid: parameters.line_group_uuid,
            valid_lines_orders,
        };

        try {
            const { job_token } =
                await getBackend2().manufacturing.runLinegroupBackwardPlanning(backward_planning_input);
            this.getPlan(job_token, parameters, next_shift_date.getTime(), dispatch);
        } catch (e) {
            dispatch({
                type: SET_RECOMMEND_PLAN_ERROR,
                data: (e as Error).message || AUTOMATIC_PLANNING_ERROR_FALLBACK,
            });
            dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
        }
    };

    // Generic function to transform line orders
    static transformLineOrders = (lineOrders: LineOrders[]): t.ILinePlanningData[] =>
        lineOrders.map(lo => ({
            hash: "hash",
            line_uuid: lo.line_uuid,
            shifts: lo.shifts,
            orders: lo.orders.map(TypeAdapter.transformOrderProducedModelFromEx), // Transform each order
            tags: {
                ...lo.tags,
                line_title: lo.line_title,
                line_index: String(lo.line_index),
                line_hash: lo.line_hash,
                line_external_id: lo.line_external_id,
            },
        }));

    static mapToObject<T>(map: Map<string, T>): { [key: string]: T } {
        const obj: { [key: string]: T } = {};
        for (const [key, value] of map) {
            obj[key] = value;
        }
        return obj;
    }

    /** Function to build report_orders_map */
    static buildReportOrdersMap = (
        orders: t2.ISimulationReportOrderLineEx[]
    ): Map<string, t2.ISimulationReportOrderEx> => {
        const map: Map<string, t2.ISimulationReportOrderEx> = new Map();
        for (const line of orders) {
            for (const order of line.production) {
                const hit = map.get(order.order_id);
                if (hit == undefined) {
                    map.set(order.order_id, order);
                }
            }
        }
        return map;
    };

    /**  Function to build report_unscheduled_orders_map */
    static buildUnscheduledOrdersMap = (
        unscheduled_orders: rt.ISimulationReportUnscheduledOrder[]
    ): Map<string, rt.ISimulationReportUnscheduledOrder> => {
        const map: Map<string, rt.ISimulationReportUnscheduledOrder> = new Map();
        for (const order of unscheduled_orders) {
            const hit = map.get(order.order_uuid);
            if (hit == undefined) {
                map.set(order.order_uuid, order);
            }
        }
        return map;
    };

    /** Function to process and override values for scheduled orders */
    static processOrders = (
        lines_orders: LineOrders[],
        report_orders_map: Map<string, t2.ISimulationReportOrderEx>,
        next_shift_date: Date
    ) => {
        for (const line of lines_orders) {
            for (const order of line.orders) {
                const report_order = report_orders_map.get(order.uuid);
                if (report_order != undefined) {
                    this.overrideOrderValues(order, report_order, next_shift_date);
                } else {
                    console.warn("report_order not found for order", order);
                }
            }
        }
    };

    /** Function to override values for an order based on the report */
    static overrideOrderValues = (
        order: IOrderProducedModelEx,
        report_order: t2.ISimulationReportOrderEx,
        next_shift_date: Date
    ) => {
        const simulation_time_start = next_shift_date.getTime();

        const estimated_start = robust_get_quantile(report_order.estimated_start, 1);
        if (estimated_start !== null) {
            order.time_start = simulation_time_start + estimated_start * TIME_RANGES.HOUR;
        } else {
            console.warn("estimated_start not defined for order", order);
        }

        const estimated_completion = robust_get_quantile(report_order.estimated_completion, 1);
        if (estimated_completion !== null) {
            order.estimated_completion = Math.floor(simulation_time_start + estimated_completion * TIME_RANGES.HOUR);
        } else {
            console.warn("estimated_completion not defined for order", order);
        }

        const estimated_duration = robust_get_quantile(report_order.estimated_duration, 1);
        if (estimated_duration !== null) {
            order.estimated_duration = estimated_duration;
        } else {
            console.warn("estimated_duration not defined for order", order);
        }
    };

    /** Function to process and override values for unscheduled orders */
    static processUnscheduledOrders = (
        unscheduled_orders: LineOrders[],
        report_unscheduled_orders_map: Map<string, rt.ISimulationReportUnscheduledOrder>
    ) => {
        for (const line of unscheduled_orders) {
            for (const order of line.orders) {
                const report_order = report_unscheduled_orders_map.get(order.uuid);
                if (report_order != undefined) {
                    const estimated_duration = robust_get_quantile(report_order.estimated_duration, 1);
                    if (estimated_duration !== null) {
                        order.estimated_duration = estimated_duration;
                    } else {
                        console.warn("estimated_duration not defined for unscheduled order", order);
                    }
                } else {
                    console.warn("report_order not found for order", order);
                }
            }
        }
    };

    static getPlan = async (
        job_token: string,
        parameters: RunBackwardPlanParameters,
        start_ts: number,
        dispatch: (args: RecommendPlanActionsLoad) => void
    ) => {
        const report_data = parameters.report_data;
        if (report_data === null) {
            throw Error("This should not happen: report_data is null");
        }

        const WAIT_INTERVAL = 500;
        const MAX_WAIT_TIME = 1000 * 60 * 10; // 10 minutes
        let wait_period = 0;

        const handleJobSuccess = async (plan_uuid: string, result: t.IGetBackwardPlanningResultRes) => {
            const mapResultToLineOrders = (line_orders: t.ILinePlanningData[]): LineOrders[] => {
                return line_orders.map(({ orders, tags, ...line_data }) => {
                    // cast t.IOrderProducedModel to IOrderProducedModelEx
                    const new_orders: IOrderProducedModelEx[] = orders.map(order => ({ ...order }));

                    // cast t.ILinePlanningData to LineOrders
                    const new_order: LineOrders = {
                        ...line_data,
                        orders: new_orders,
                        line_title: tags.line_title ?? "",
                        line_hash: tags.line_hash ?? "",
                        line_index: Number(tags.line_index ?? 0),
                        line_external_id: tags.line_external_id ?? "",
                        tags,
                    };

                    return new_order;
                });
            };

            const lines_orders: LineOrders[] = mapResultToLineOrders(result.line_orders);
            const unscheduled_orders: LineOrders[] = mapResultToLineOrders(result.unscheduled_orders);

            // close running dialog
            dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
            // close parameter dialog
            dispatch({
                type: SET_RECOMMEND_PLAN_MODAL,
                data: {
                    show_recommend_param_dialog: false,
                },
            });

            dispatch({
                type: UPDATE_ALL_ORDERS_RECOMMEND_PLAN,
                data: {
                    unscheduled_orders,
                    lines_orders,
                    plan_uuid,
                    selected_line_uuids: parameters.line_uuids,
                },
            });

            return result;
        };

        let error_counter = 0;
        const MAX_RETRY = 3;
        const waitForJob = async (plan_uuid: string): Promise<t.IGetBackwardPlanningResultRes | null> => {
            try {
                const { status } = await getBackend2().manufacturing.getPlanStatus({ plan_uuid });

                switch (status.status) {
                    case JOB_STATUS.ended: {
                        const result = await getBackend2().manufacturing.getBackwardPlanningResult({ plan_uuid });
                        return handleJobSuccess(plan_uuid, result);
                    }
                    case JOB_STATUS.errored:
                        dispatch({
                            type: SET_RECOMMEND_PLAN_ERROR,
                            data: status.status_msg || "Automatic planning job failed.",
                        });
                        dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
                        console.error(`Job errored (plan_uuid: ${plan_uuid})`);
                        return null;

                    default:
                        if (wait_period >= MAX_WAIT_TIME) {
                            console.error(`Automatic planning timed out (plan_uuid: ${plan_uuid})`);
                            dispatch({
                                type: SET_RECOMMEND_PLAN_ERROR,
                                data: "Automatic planning timed out.",
                            });
                            dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
                            return null;
                        }
                        wait_period += WAIT_INTERVAL;
                        await new Promise(resolve => setTimeout(resolve, WAIT_INTERVAL));
                        return await waitForJob(plan_uuid);
                }
            } catch (error) {
                error_counter++;
                console.error(`Error during automatic planning (plan_uuid: ${plan_uuid}):`, error);
                if (error_counter > MAX_RETRY) {
                    dispatch({
                        type: SET_RECOMMEND_PLAN_ERROR,
                        data: (error as Error).message || AUTOMATIC_PLANNING_ERROR_FALLBACK,
                    });
                    dispatch({ type: SET_RECOMMEND_PLAN_RUNNING, data: false });
                    return null;
                } else {
                    console.error(`Retry ${error_counter} of ${MAX_RETRY}`);
                    return await waitForJob(plan_uuid);
                }
            }
        };

        await waitForJob(job_token);
    };
}

type SetRescheduleOrderActions =
    | SetRescheduleOrderAction
    | SetHighlightedOrders
    | ResetFiltersAction
    | SetLastClickedOrderUuid
    | SetRescheduleOrderAction;

export class ReduxFilters {
    static planning_table_store: Store<ReduxState> | null = null;
    static last_click_location: "gantt" | "line_order_list" | null = null;

    static isDroppableLineUuid = (line_uuid: string, order_uuid: string | null, is_unscheduled: boolean): boolean => {
        if (order_uuid && !is_unscheduled) {
            const line_uuids = ValidLinesOrdersHelper.getLineUuids(order_uuid);
            if (line_uuids) {
                return line_uuids.includes(line_uuid);
            } else {
                // might be a chunk order
                const order = LinesOrdersMaps.all_orders_map.get(order_uuid);
                if (order && order.parent_order_uuid) {
                    const parent_line_uuids = ValidLinesOrdersHelper.getLineUuids(order.parent_order_uuid);
                    if (parent_line_uuids) {
                        return parent_line_uuids.includes(line_uuid);
                    }
                }
            }
        }
        return false;
    };

    static onUnmount() {
        ReduxFilters.last_click_location = null;
        DraggableLogic.changeMouseCursor(null);
    }

    static isLineLocked = () => {
        return document.querySelector(`#${LOCKED_LINE_ID}[data-enabled="true"]`);
    };

    static setSelectedOrderUuid = async (
        reduxDispatch: (args: SetRescheduleOrderActions) => void,
        order_uuid: string,
        click_location: "gantt" | "line_order_list"
    ) => {
        ReduxFilters.last_click_location = click_location;
        reduxDispatch({ type: SET_LAST_CLICKED_ORDER_UUID, data: order_uuid });
    };

    static setHighlightedOrders = async (order_uuid: string): Promise<Map<string, OrderHighlights>> => {
        const related_lines = await ReduxFilters.getRelatedLines(order_uuid);
        // collect orders which should be highlighted as related
        const highlighted_orders_map: Map<string, OrderHighlights> = new Map();
        for (const input_line of related_lines.input_lines) {
            input_line.orders.forEach(o =>
                highlighted_orders_map.set(o.order_uuid, { is_highlighted: true, type: "input" })
            );
        }
        for (const order_line of related_lines.order_lines) {
            order_line.orders.forEach(o =>
                highlighted_orders_map.set(o.order_uuid, { is_highlighted: true, type: "order" })
            );
        }
        for (const output_line of related_lines.output_lines) {
            output_line.orders.forEach(o =>
                highlighted_orders_map.set(o.order_uuid, { is_highlighted: true, type: "output" })
            );
        }
        return highlighted_orders_map;
    };

    static updateHeights = (
        valid_lines_map: Map<string, ValidLineOrders>,
        valid_orders_map: Map<string, ValidOrder>,
        line_group_uuid: string,
        report_data: t2.IReportModelEx,
        unscheduled_orders: LineOrders[],
        waterfall_mode: boolean
    ) => {
        const count_non_empty_lines = countRowsOnGantt({
            valid_lines_map,
            line_group_uuid: line_group_uuid,
            report_data: report_data,
            source_type: GanttChartSource.planning_table_parallel,
            count_orders: waterfall_mode,
        });
        const num_unscheduled_lines = countUnscheduledLinesOnGantt({
            valid_orders_map,
            unscheduled_orders: unscheduled_orders,
            source_type: GanttChartSource.planning_table_parallel,
        });
        return { count_non_empty_lines, num_unscheduled_lines };
    };

    static setRescheduleOrderUuid = async (
        reduxDispatch: (args: SetRescheduleOrderBundleAction) => void,
        order_uuid: string,
        click_location: "gantt" | "line_order_list",
        is_filter_locked: boolean,
        order_sort = false
    ) => {
        ReduxFilters.last_click_location = click_location;
        const highlighted_orders_map = await ReduxFilters.setHighlightedOrders(order_uuid);
        const store = ReduxFilters.planning_table_store;
        if (!store) {
            console.log("No store found");
            return;
        }

        const state: ReduxState = store.getState();
        const lines_orders = state.gantt_chart_lines_orders.lines_orders;
        const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
        const report = state.gantt_chart_report;
        const report_data = report.report_data;
        const original_report_created_at = report.original_report_created_at;
        const recommend_explanations = state.gantt_chart_planning_table.recommend_plan.explanations;

        if (!report_data || !original_report_created_at) {
            return;
        }
        const report_lines = report_data.result.orders;
        const properties = state.gantt_chart_properties;
        const filters = state.gantt_chart_filters;
        const materials = state.gantt_chart.materials;
        const insights_mapped = state.gantt_chart_insights.insights_mapped;
        if (!insights_mapped) {
            return null;
        }
        const line_group_uuid = properties.line_group_uuid;
        if (!line_group_uuid) {
            return;
        }

        filters.is_filter_locked = is_filter_locked;

        if (order_sort) {
            return;
        }

        if (!is_filter_locked) {
            filters.reschedule_order_uuid = order_uuid;
            filters.selected_filter_type = TypeFilterValues.reschedule_operation;
            // compute what can be shown
            const { valid_lines, valid_orders } = await ReduxFilters.applyFilters(
                lines_orders,
                filters,
                unscheduled_orders,
                materials,
                insights_mapped,
                report_lines,
                original_report_created_at,
                recommend_explanations
            );
            // prepare maps for quick access
            const valid_lines_map = new Map<string, ValidLineOrders>();
            for (const valid_line of valid_lines) {
                valid_lines_map.set(valid_line.line_uuid, valid_line);
            }
            const valid_orders_map = new Map<string, ValidOrder>();
            for (const valid_order of valid_orders) {
                valid_orders_map.set(valid_order.uuid, valid_order);
            }
            const show_valid_lines = {
                valid_lines,
                valid_lines_map,
                valid_orders_map,
            };
            const { count_non_empty_lines, num_unscheduled_lines } = ReduxFilters.updateHeights(
                valid_lines_map,
                valid_orders_map,
                line_group_uuid,
                report_data,
                unscheduled_orders,
                properties.waterfall_mode
            );
            // add to state
            reduxDispatch({
                type: SET_RESCHEDULE_ORDER_BUNDLE,
                data: {
                    show_valid_lines,
                    order_uuid,
                    count_non_empty_lines,
                    num_unscheduled_lines,
                    is_filter_locked,
                    highlighted_orders: { highlighted_orders_map },
                },
            });
        } else {
            // add to state
            reduxDispatch({
                type: SET_RESCHEDULE_ORDER_BUNDLE,
                data: {
                    show_valid_lines: null,
                    order_uuid,
                    count_non_empty_lines: null,
                    num_unscheduled_lines: null,
                    is_filter_locked,
                    highlighted_orders: { highlighted_orders_map },
                },
            });
        }
    };

    static isValidOrder = (valid_lines: ValidLineOrders[] | null, order_uuid: string | null): boolean => {
        // fetching valid_lines straight from store otherwise if we pass through props
        // it might result in performance issues on gantt chart
        if (valid_lines === null) return true;

        for (const line_order of valid_lines) {
            for (const o of line_order.orders) {
                if (o.uuid === order_uuid) {
                    return true;
                }
            }
        }
        return false;
    };

    static getRescheduleLineOrderUuids = async (
        reschedule_line_uuid: string,
        report_created_at: number
    ): Promise<string[]> => {
        try {
            const res = await getBackend2().manufacturing.getLinePossibleOrders({
                line_uuid: reschedule_line_uuid,
                report_created_at,
            });
            return res.order_uuids;
        } catch (e) {
            console.error(e);
        }
        return [];
    };

    static getRescheduleOrderLineUuids = async (reschedule_order_uuid: string): Promise<string[]> => {
        try {
            const res = await getBackend2().manufacturing.getOrderLines({ uuid: reschedule_order_uuid });
            return res.lines.map(l => l.uuid);
        } catch (e) {
            console.error(e);
        }
        return [];
    };

    static applyNoFilter = (lines_orders: LineOrders[], unscheduled_orders: LineOrders[]): FilterValidLinesOrders => {
        // list of lines, that pass filter
        // - keeps only lines that are selected in filter
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        // return all planned lines and all unscheduled orders
        for (const line_orders of [...lines_orders, ...unscheduled_orders]) {
            // take all orders
            for (const order of line_orders.orders) {
                valid_orders.push({
                    uuid: order.uuid,
                    external_id: order.external_id,
                    material_external_id: order.material_external_id,
                    earliest_start: order.earliest_start,
                    is_translucent: false,
                });
            }
            // package into valid line
            valid_lines.push({
                line_title: line_orders.line_title,
                line_uuid: line_orders.line_uuid,
                line_external_id: line_orders.line_external_id,
                tags: line_orders.tags,
                is_extra: false,
                orders: [],
            });
        }

        return { valid_lines, valid_orders };
    };

    static applyRescheduleLineFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        original_report_created_at: number,
        reschedule_line_uuid: string
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keeps only lines that are selected in filter
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        //  - Find all operations that can be scheduled on the line.
        //  - Select all lines on which these operations are currently scheduled
        //    and selected them among valid lines. Operations on these other lines
        //    that cannot be scheduled on the selected line, are grayed out on Gantt.
        //    LoL list show all orders for valid lines. Grayed out orders are 1:1
        //    with grayed out operations on Gantt.
        //  - Unscheduled operations are filtered out to only show on Gantt operations
        //    which can be scheduled on the selected line. LoL list show all orders and
        //    unscheduled orders that cannot be moved to the selected line are grayed out.

        // 1. Find all operations that can be scheduled on the line.
        // We need to pass the timestamp of when we loaded the planning board in order
        // to compensate for orders that were open then and closed now in the database.
        // Function returns both scheduled and unscheduled orders.
        const valid_order_uuids = ValidLinesOrdersHelper.getOperationUuids(reschedule_line_uuid);
        // we make a set for fast lookup
        const valid_order_uuids_set = new Set(valid_order_uuids);

        // 2. Select all lines on which these operations are currently scheduled
        for (const line_orders of lines_orders) {
            // check if at least one order on the line is valid
            let has_valid_order = false;
            for (const scheduled_order of line_orders.orders) {
                if (valid_order_uuids_set.has(scheduled_order.uuid)) {
                    has_valid_order = true;
                    break;
                }
            }
            // if yes, add the line to the list of valid lines
            if (has_valid_order) {
                // map orders to valid orders
                for (const order of line_orders.orders) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: !valid_order_uuids_set.has(order.uuid),
                    });
                }
                // package into valid line
                valid_lines.push({
                    line_title: line_orders.line_title,
                    line_uuid: line_orders.line_uuid,
                    line_external_id: line_orders.line_external_id,
                    tags: line_orders.tags,
                    is_extra: false,
                    orders: [],
                });
            } else if (line_orders.line_uuid === reschedule_line_uuid) {
                // line we are filtering for has no scheduled orders
                // however we must still show it
                valid_lines.push({
                    line_title: line_orders.line_title,
                    line_uuid: line_orders.line_uuid,
                    line_external_id: line_orders.line_external_id,
                    tags: line_orders.tags,
                    is_extra: false,
                    orders: [],
                });
            }
        }

        // 3. Unscheduled operations are filtered out to only show on Gantt operations
        for (const unscheduled_line_orders of unscheduled_orders) {
            // filter orders
            for (const unscheduled_order of unscheduled_line_orders.orders) {
                if (valid_order_uuids_set.has(unscheduled_order.uuid)) {
                    valid_orders.push({
                        uuid: unscheduled_order.uuid,
                        external_id: unscheduled_order.external_id,
                        material_external_id: unscheduled_order.material_external_id,
                        earliest_start: unscheduled_order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static applyRescheduleOrderFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        report_lines: t2.ISimulationReportOrderLineEx[],
        reschedule_order_uuid: string
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keeps only lines that are selected in filter
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        // - Find all lines on which the operation can be scheduled
        // - Select all lines on which the operation can be scheduled as valid lines
        // - On selected lines, keep other operations as grayed out operations (translucent)
        // - Add extra related lines with as read-only with connected operations highlighted
        // 1. Find all valid lines for the operation
        let reschedule_order_uuid_mapped = reschedule_order_uuid;

        // check if operatin is already in the matrix
        let line_uuids = ValidLinesOrdersHelper.getLineUuids(reschedule_order_uuid_mapped);
        if (line_uuids === null) {
            // if the operation is not in the matrix, check if it is a chunk order
            if (LinesOrdersMaps.chunk_orders.has(reschedule_order_uuid)) {
                const chunk_order = LinesOrdersMaps.chunk_orders.get(reschedule_order_uuid);
                if (chunk_order && chunk_order.parent_order_uuid) {
                    // if the chunk order has a parent, use the parent uuid to get the line_uuids
                    reschedule_order_uuid_mapped = chunk_order.parent_order_uuid;
                    line_uuids = ValidLinesOrdersHelper.getLineUuids(reschedule_order_uuid_mapped);
                }
            }

            if (LinesOrdersLogic.isConvertedOperation(reschedule_order_uuid_mapped)) {
                // if the operation is a converted operation, find line UUIDs for the parent operation
                reschedule_order_uuid_mapped =
                    LinesOrdersLogic.getConvertedOperationParentId(reschedule_order_uuid_mapped);
                line_uuids = ValidLinesOrdersHelper.getLineUuids(reschedule_order_uuid_mapped);
            }
        }

        // we make a set for fast lookup
        const line_uuids_set = new Set(line_uuids);
        // make sure that if the order is scheduled its current line is included
        for (const line_orders of lines_orders) {
            let found_selected_order_in_line = false;
            for (const order of line_orders.orders) {
                if (order.uuid === reschedule_order_uuid_mapped) {
                    line_uuids_set.add(line_orders.line_uuid);
                    found_selected_order_in_line = true;
                    break;
                }
            }
            if (found_selected_order_in_line) {
                break;
            }
        }

        // 2. Select all lines on which the operation can be scheduled as valid lines
        // make sure that at least the current line of the order appears
        let found_selected_order = false;
        for (const line_orders of lines_orders) {
            if (line_uuids_set.has(line_orders.line_uuid)) {
                // valid line, add it to the list
                valid_lines.push({
                    line_title: line_orders.line_title,
                    line_uuid: line_orders.line_uuid,
                    line_external_id: line_orders.line_external_id,
                    tags: line_orders.tags,
                    is_extra: false,
                    orders: [],
                });
                // go over orders and add them to the list as translucent
                for (const order of line_orders.orders) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: order.uuid !== reschedule_order_uuid_mapped,
                    });
                    // check if we found the selected order
                    if (order.uuid === reschedule_order_uuid_mapped) {
                        found_selected_order = true;
                    }
                }
            }
        }

        // 3. if we did not find the selected order, check if we have it among unscheduled orders
        if (!found_selected_order) {
            for (const unscheduled_line_orders of unscheduled_orders) {
                for (const unscheduled_order of unscheduled_line_orders.orders) {
                    if (unscheduled_order.uuid === reschedule_order_uuid_mapped) {
                        // we found the order, add it to the list
                        found_selected_order = true;
                        valid_orders.push({
                            uuid: unscheduled_order.uuid,
                            external_id: unscheduled_order.external_id,
                            material_external_id: unscheduled_order.material_external_id,
                            earliest_start: unscheduled_order.earliest_start,
                            is_translucent: false,
                        });
                        break;
                    }
                }
                if (found_selected_order) {
                    break;
                }
            }
        }

        // 4. collect related lines

        const related_lines = await ReduxFilters.getRelatedLines(reschedule_order_uuid_mapped);
        const all_related_lines = [
            ...related_lines.input_lines,
            ...related_lines.output_lines,
            ...related_lines.order_lines,
        ];

        // lines that come from the backend might be different to the current position on the planning board
        // we need to find the current position for each order and only show those lines
        // we also show connected unscheduled orders
        const related_order_set: Set<string> = new Set();
        for (const related_line of all_related_lines) {
            for (const order of related_line.orders) {
                related_order_set.add(order.order_uuid);
            }
        }

        // Set of uuids of lines in current line group
        const line_group_lines_uuids_set: Set<string> = new Set(lines_orders.map(line => line.line_uuid));

        // we go over all lines returned by report to see if we have any related lines from other line-groups
        for (const report_line of report_lines) {
            // if the line is already included, no need to bother
            if (line_uuids_set.has(report_line.line)) {
                continue;
            }
            // if the line isn not yet included, check if it has at least one related order
            let found_related_order = false;
            for (const order of report_line.production) {
                if (related_order_set.has(order.order_id)) {
                    found_related_order = true;
                    break;
                }
            }
            if (!found_related_order) {
                continue;
            }
            // if the line has at least one related order, add it to the list
            valid_lines.push({
                line_title: report_line.line_title,
                line_uuid: report_line.line,
                line_external_id: "",
                tags: {},
                is_extra: !line_group_lines_uuids_set.has(report_line.line),
                orders: [],
            });

            // since line is not yet included, we need to remember also orders
            for (const order of report_line.production) {
                valid_orders.push({
                    uuid: order.order_id,
                    external_id: order.order_external_id,
                    material_external_id: "",
                    earliest_start: 0,
                    is_translucent: true,
                });
            }
        }

        // check if also any unscheduled order should be included
        for (const unscheduled_line_orders of unscheduled_orders) {
            for (const unscheduled_order of unscheduled_line_orders.orders) {
                if (related_order_set.has(unscheduled_order.uuid)) {
                    // we found the order, add it to the list
                    found_selected_order = true;
                    valid_orders.push({
                        uuid: unscheduled_order.uuid,
                        external_id: unscheduled_order.external_id,
                        material_external_id: unscheduled_order.material_external_id,
                        earliest_start: unscheduled_order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static applyMaterialFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        selected_materials: string[]
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        for (const line_orders of lines_orders) {
            // map orders to valid orders
            for (const order of line_orders.orders) {
                valid_orders.push({
                    uuid: order.uuid,
                    external_id: order.external_id,
                    material_external_id: order.material_external_id,
                    earliest_start: order.earliest_start,
                    is_translucent: !selected_materials.includes(order.material_external_id),
                });
            }
            // package into valid line
            valid_lines.push({
                line_title: line_orders.line_title,
                line_uuid: line_orders.line_uuid,
                line_external_id: line_orders.line_external_id,
                tags: line_orders.tags,
                is_extra: false,
                orders: [],
            });
        }

        // keep all unscheduled operations producting the material
        for (const line_orders of unscheduled_orders) {
            // take all orders
            for (const order of line_orders.orders) {
                if (selected_materials.includes(order.material_external_id)) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static applyLineFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[] = [],
        selecte_line_uuids: string[]
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keeps only lines that are selected in filter
        // - keeps only relevant unscheduled orders
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];
        const valid_order_uuids: string[] = [];

        for (const line_orders of lines_orders) {
            if (selecte_line_uuids.includes(line_orders.line_uuid)) {
                const valid_line_order_uuids = ValidLinesOrdersHelper.getOperationUuids(line_orders.line_uuid) || [];
                valid_order_uuids.push(...valid_line_order_uuids);
                // map orders to valid orders
                for (const order of line_orders.orders) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
                // package into valid line
                valid_lines.push({
                    line_title: line_orders.line_title,
                    line_uuid: line_orders.line_uuid,
                    line_external_id: line_orders.line_external_id,
                    tags: line_orders.tags,
                    is_extra: false,
                    orders: [],
                });
            }
        }

        const valid_order_uuids_set = new Set(valid_order_uuids);

        for (const line_orders of unscheduled_orders) {
            for (const order of line_orders.orders) {
                // Filter unscheduled orders
                if (valid_order_uuids_set.has(order.uuid)) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static applyOrderFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        selected_order_external_ids: string[]
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keep all lines
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        for (const line_orders of lines_orders) {
            // map orders to valid orders
            for (const order of line_orders.orders) {
                valid_orders.push({
                    uuid: order.uuid,
                    external_id: order.external_id,
                    material_external_id: order.material_external_id,
                    earliest_start: order.earliest_start,
                    is_translucent: !selected_order_external_ids.includes(order.external_id),
                });
            }
            // package into valid line
            valid_lines.push({
                line_title: line_orders.line_title,
                line_uuid: line_orders.line_uuid,
                line_external_id: line_orders.line_external_id,
                tags: line_orders.tags,
                is_extra: false,
                orders: [],
            });
        }

        // keep all unscheduled operations producting the material
        for (const line_orders of unscheduled_orders) {
            // take all orders
            for (const order of line_orders.orders) {
                if (selected_order_external_ids.includes(order.external_id)) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static filterByValues = (
        values: Set<string>,
        value: string,
        input_type: CustomFilterInputType,
        match: CustomFilterMatch
    ) => {
        if (values.size === 0) {
            return true;
        }

        if (value === "") {
            return false;
        }

        if (input_type === CUSTOM_FILTER_INPUT_TYPES.datetime) {
            const selected_ts = Number([...values][0]);
            if (Number.isNaN(selected_ts)) {
                return true;
            }

            const ts = Number(value);
            if (Number.isNaN(ts)) {
                return false;
            }

            if (match === CUSTOM_FILTER_MATCH.less_than) {
                return ts < selected_ts;
            }

            if (match === CUSTOM_FILTER_MATCH.less_than_or_equal) {
                return ts <= selected_ts;
            }

            if (match === CUSTOM_FILTER_MATCH.greater_than) {
                return ts > selected_ts;
            }

            if (match === CUSTOM_FILTER_MATCH.greater_than_or_equal) {
                return ts >= selected_ts;
            }

            return false;
        }

        // Default: exact match with one of the selected values
        return values.has(value);
    };

    static applyCustomFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        materials: t.IMaterialModel[],
        selected_custom_filter_entity: CustomFilterEntity,
        selected_custom_filter_field: string,
        selected_custom_filter_values: string[],
        selected_custom_filter_input_type: CustomFilterInputType,
        selected_custom_filter_match: CustomFilterMatch
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keep all lines
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        const materials_map: Map<string, t.IMaterialModel> = new Map(
            materials.map(material => [material.uuid, material])
        );
        const selected_values_set = new Set(selected_custom_filter_values);
        const is_tag_field = selected_custom_filter_field.startsWith("tags.");
        const field_name = is_tag_field ? selected_custom_filter_field.slice(5) : selected_custom_filter_field;
        for (const line_orders of lines_orders) {
            for (const order of line_orders.orders) {
                let value: string | undefined;
                if (selected_custom_filter_entity === CUSTOM_FILTER_ENTITIES.order) {
                    value = is_tag_field ? order.tags[field_name] : order[field_name];
                } else if (selected_custom_filter_entity === CUSTOM_FILTER_ENTITIES.material) {
                    const material = materials_map.get(order.material_uuid);
                    if (material !== undefined) {
                        value = is_tag_field ? material.tags[field_name] : material[field_name];
                    }
                }

                valid_orders.push({
                    uuid: order.uuid,
                    external_id: order.external_id,
                    material_external_id: order.material_external_id,
                    earliest_start: order.earliest_start,
                    is_translucent: !this.filterByValues(
                        selected_values_set,
                        value !== undefined ? String(value) : "",
                        selected_custom_filter_input_type,
                        selected_custom_filter_match
                    ),
                });
            }

            valid_lines.push({
                line_title: line_orders.line_title,
                line_uuid: line_orders.line_uuid,
                line_external_id: line_orders.line_external_id,
                tags: line_orders.tags,
                is_extra: false,
                orders: [],
            });
        }

        // Keep all unscheduled operations producing the material
        for (const line_orders of unscheduled_orders) {
            for (const order of line_orders.orders) {
                let value: string | undefined;
                if (selected_custom_filter_entity === CUSTOM_FILTER_ENTITIES.order) {
                    value = is_tag_field ? order.tags[field_name] : order[field_name];
                } else if (selected_custom_filter_entity === CUSTOM_FILTER_ENTITIES.material) {
                    const material = materials_map.get(order.material_uuid);
                    if (material !== undefined) {
                        value = is_tag_field ? material.tags[field_name] : material[field_name];
                    }
                }

                if (
                    this.filterByValues(
                        selected_values_set,
                        value !== undefined ? String(value) : "",
                        selected_custom_filter_input_type,
                        selected_custom_filter_match
                    )
                ) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static applyInsightTypeFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        insights_mapped: Map<string, t.IEventDataEx[]>,
        selected_insight_type: string
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keep all lines
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];

        for (const line_orders of lines_orders) {
            // map orders to valid orders
            for (const order of line_orders.orders) {
                const insights = insights_mapped.get(order.uuid) || [];
                valid_orders.push({
                    uuid: order.uuid,
                    external_id: order.external_id,
                    material_external_id: order.material_external_id,
                    earliest_start: order.earliest_start,
                    is_translucent: !insights.some(i => i.type === selected_insight_type),
                });
            }
            // package into valid line
            valid_lines.push({
                line_title: line_orders.line_title,
                line_uuid: line_orders.line_uuid,
                line_external_id: line_orders.line_external_id,
                tags: line_orders.tags,
                is_extra: false,
                orders: [],
            });
        }

        // keep all unscheduled operations producting the material
        for (const line_orders of unscheduled_orders) {
            // take all orders
            for (const order of line_orders.orders) {
                const insights = insights_mapped.get(order.uuid) || [];
                if (insights.some(i => i.type === selected_insight_type)) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }

        return { valid_lines, valid_orders };
    };

    static applyPlanningResultFilter = async (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        selected_planning_result_type: string,
        recommend_plans: t.IPlanExplanation[]
    ): Promise<FilterValidLinesOrders> => {
        // list of lines, that pass filter
        // - keep all lines
        // - keeps all orders for each line but highlight according to the filter
        const valid_lines: ValidLineOrders[] = [];
        const valid_orders: ValidOrder[] = [];
        type ExplanationData = { type: string; extra_data: ExtraData };

        const explanations = new Map<string, ExplanationData>();

        recommend_plans.forEach(p => {
            const data: ExplanationData = { type: p.type, extra_data: p.extra_data };
            explanations.set(p.operation_uuid, data);
        });

        for (const line_orders of lines_orders) {
            // map orders to valid orders
            for (const order of line_orders.orders) {
                const explanation_data = explanations.get(order.uuid);
                let is_translucent = true;

                if (explanation_data != undefined) {
                    is_translucent = explanation_data.type !== selected_planning_result_type;
                    if (selected_planning_result_type === "offset_previous_operation") {
                        if (
                            explanation_data.type === PLANNING_EXPLANATION_TYPE.scheduled &&
                            explanation_data.extra_data.offset_previous_operation
                        ) {
                            is_translucent = false;
                        }
                    }

                    if (selected_planning_result_type === "offset_material_external_id") {
                        if (
                            explanation_data.type === PLANNING_EXPLANATION_TYPE.scheduled &&
                            explanation_data.extra_data.offset_material_external_id
                        ) {
                            is_translucent = false;
                        }
                    }
                }

                valid_orders.push({
                    uuid: order.uuid,
                    external_id: order.external_id,
                    material_external_id: order.material_external_id,
                    earliest_start: order.earliest_start,
                    is_translucent,
                });
            }
            // package into valid line
            valid_lines.push({
                line_title: line_orders.line_title,
                line_uuid: line_orders.line_uuid,
                line_external_id: line_orders.line_external_id,
                tags: line_orders.tags,
                is_extra: false,
                orders: [],
            });
        }

        for (const line_orders of unscheduled_orders) {
            for (const order of line_orders.orders) {
                const explanation_data = explanations.get(order.uuid);
                let is_valid = explanation_data != undefined && explanation_data.type === selected_planning_result_type;

                if (is_valid) {
                    valid_orders.push({
                        uuid: order.uuid,
                        external_id: order.external_id,
                        material_external_id: order.material_external_id,
                        earliest_start: order.earliest_start,
                        is_translucent: false,
                    });
                }
            }
        }
        return { valid_lines, valid_orders };
    };

    static applyFilters = async (
        lines_orders: LineOrders[],
        filters: PlanningTableFiltersState,
        unscheduled_orders: LineOrders[],
        materials: t.IMaterialModel[],
        insights_mapped: Map<string, t.IEventDataEx[]>,
        report_lines: t2.ISimulationReportOrderLineEx[],
        original_report_created_at: number,
        recommend_explanations: t.IPlanExplanation[]
    ): Promise<FilterValidLinesOrders> => {
        if (BusinessLogic.getSysFlag("env") === "dev") {
            console.log("Applying filters:", filters);
        }
        if (
            filters.selected_filter_type === TypeFilterValues.reschedule_line &&
            filters.reschedule_line_uuid !== null
        ) {
            // RESCHEDULE ON LINE
            return await ReduxFilters.applyRescheduleLineFilter(
                lines_orders,
                unscheduled_orders,
                original_report_created_at,
                filters.reschedule_line_uuid
            );
        } else if (
            filters.selected_filter_type === TypeFilterValues.reschedule_operation &&
            filters.reschedule_order_uuid !== null
        ) {
            // RESCHEDULE OPERATION
            return await ReduxFilters.applyRescheduleOrderFilter(
                lines_orders,
                unscheduled_orders,
                report_lines,
                filters.reschedule_order_uuid
            );
        } else if (
            filters.selected_filter_type === TypeFilterValues.material &&
            filters.selected_materials.length > 0
        ) {
            // MATERIAL FILTER
            return await ReduxFilters.applyMaterialFilter(lines_orders, unscheduled_orders, filters.selected_materials);
        } else if (filters.selected_filter_type === TypeFilterValues.line && filters.selected_line_uuids.length > 0) {
            // LINE FILTER
            return await ReduxFilters.applyLineFilter(lines_orders, unscheduled_orders, filters.selected_line_uuids);
        } else if (
            filters.selected_filter_type === TypeFilterValues.order &&
            filters.selected_order_external_ids.length > 0
        ) {
            // OPERATION FILTER
            return await ReduxFilters.applyOrderFilter(
                lines_orders,
                unscheduled_orders,
                filters.selected_order_external_ids
            );
        } else if (
            filters.selected_filter_type === TypeFilterValues.custom &&
            filters.selected_custom_filter_entity !== null &&
            filters.selected_custom_filter_field !== null &&
            filters.selected_custom_filter_input_type !== null &&
            filters.selected_custom_filter_match !== null &&
            filters.selected_custom_filter_values.length > 0
        ) {
            // CUSTOM FILTER
            return await ReduxFilters.applyCustomFilter(
                lines_orders,
                unscheduled_orders,
                materials,
                filters.selected_custom_filter_entity,
                filters.selected_custom_filter_field,
                filters.selected_custom_filter_values,
                filters.selected_custom_filter_input_type,
                filters.selected_custom_filter_match
            );
        } else if (
            filters.selected_filter_type === TypeFilterValues.order_insight_type &&
            filters.selected_insight_type !== null
        ) {
            // INSIGHT TYPE FILTER
            return await ReduxFilters.applyInsightTypeFilter(
                lines_orders,
                unscheduled_orders,
                insights_mapped,
                filters.selected_insight_type
            );
        } else if (
            filters.selected_filter_type === TypeFilterValues.planning_result_filter &&
            filters.selected_planning_result_type !== null
        ) {
            // PLANNING RESULT TYPE FILTER
            return await ReduxFilters.applyPlanningResultFilter(
                lines_orders,
                unscheduled_orders,
                filters.selected_planning_result_type || "",
                recommend_explanations
            );
        }

        // NO FILTER
        return ReduxFilters.applyNoFilter(lines_orders, unscheduled_orders);
    };

    static related_lines_cache = new Map<string, t.IRelatedLinesForOrder | undefined>();

    static fixRelatedLinesForConvertedOperation = (
        related_lines: t.IRelatedLinesForOrder
        // uuid: string
    ): t.IRelatedLinesForOrder => {
        const store = ReduxFilters.planning_table_store;
        if (!store) {
            return related_lines;
        }

        const state: ReduxState = store.getState();
        const lines_orders = state.gantt_chart_lines_orders.lines_orders;
        const lines_orders_map: Map<string, LineOrders> = new Map(lines_orders.map(l => [l.line_uuid, l]));
        const converted_related_lines: t.IRelatedLinesForOrder = deepClone(related_lines);
        const order_lines_map: Map<string, t.IRelatedLine> = new Map();

        // Fix related converted operations from the same order:
        // - preserve empty related lines
        // - replace original (parent) operations with converted (child) operations
        for (const related_line of converted_related_lines.order_lines) {
            // Preserve empty related line
            if (related_line.orders.length === 0) {
                if (!order_lines_map.has(related_line.line_uuid)) {
                    order_lines_map.set(related_line.line_uuid, related_line);
                }

                continue;
            }

            for (const operation of related_line.orders) {
                if (!LinesOrdersLogic.isConvertedOperationParent(operation.order_uuid)) {
                    // Preserve original operation (should probably not happen
                    // since all order operations should be converted)
                    let line = order_lines_map.get(related_line.line_uuid);
                    if (line === undefined) {
                        line = {
                            ...related_line,
                            orders: [],
                        };
                        order_lines_map.set(related_line.line_uuid, line);
                    }

                    line.orders.push(operation);
                } else {
                    // Replace original (parent) operation with converted (child) operation
                    const converted_order_uuid = LinesOrdersLogic.generateConvertedOrderId(operation.order_uuid);
                    const converted_order_external_id = LinesOrdersLogic.generateConvertedOrderId(
                        operation.order_external_id
                    );
                    let line_uuid = related_line.line_uuid;
                    let line_external_id = related_line.line_external_id;
                    let line_title = related_line.line_title;
                    let converted_operation: IOrderProducedModelEx | t.IOrderModelBase | undefined =
                        LinesOrdersMaps.converted_operations.get(converted_order_uuid);
                    if (converted_operation !== undefined) {
                        const line_orders = lines_orders_map.get(converted_operation.line_uuid);
                        if (line_orders !== undefined) {
                            line_uuid = line_orders.line_uuid;
                            line_external_id = line_orders.line_external_id;
                            line_title = line_orders.line_title;
                        }
                    } else {
                        converted_operation = LinesOrdersMaps.converted_extra_operations.get(converted_order_uuid);
                        if (converted_operation !== undefined) {
                            line_uuid = converted_operation.line.uuid;
                            line_external_id = converted_operation.line.external_id;
                            line_title = converted_operation.line.title;
                        }
                    }

                    let line = order_lines_map.get(line_uuid);
                    if (line === undefined) {
                        line = {
                            line_uuid,
                            line_external_id,
                            line_title,
                            orders: [],
                        };

                        order_lines_map.set(line_uuid, line);
                    }

                    operation.order_uuid = converted_order_uuid;
                    operation.order_external_id = converted_order_external_id;
                    line.orders.push(operation);
                }
            }
        }

        converted_related_lines.order_lines = [...order_lines_map.values()];
        order_lines_map.clear();

        // Fix related converted operations from input and output lines:
        // - preserve original operations
        // - when operation was converted, also add converted operations to corresponding lines
        for (const lines of [converted_related_lines.input_lines, converted_related_lines.output_lines]) {
            for (const related_line of lines) {
                // Preserve original line
                let current_line = order_lines_map.get(related_line.line_uuid);
                if (current_line === undefined) {
                    current_line = {
                        ...related_line,
                        orders: [],
                    };
                    order_lines_map.set(related_line.line_uuid, current_line);
                }

                for (const operation of related_line.orders) {
                    // Preserve original operation
                    current_line.orders.push(operation);

                    // If operation was converted, also add converted operation to corresponding line
                    if (LinesOrdersLogic.isConvertedOperationParent(operation.order_uuid)) {
                        const converted_order_uuid = LinesOrdersLogic.generateConvertedOrderId(operation.order_uuid);
                        const converted_order_external_id = LinesOrdersLogic.generateConvertedOrderId(
                            operation.order_external_id
                        );
                        let line_uuid = related_line.line_uuid;
                        let line_external_id = related_line.line_external_id;
                        let line_title = related_line.line_title;
                        let converted_operation: IOrderProducedModelEx | t.IOrderModelBase | undefined =
                            LinesOrdersMaps.converted_operations.get(converted_order_uuid);
                        if (converted_operation !== undefined) {
                            const line_orders = lines_orders_map.get(converted_operation.line_uuid);
                            if (line_orders !== undefined) {
                                line_uuid = line_orders.line_uuid;
                                line_external_id = line_orders.line_external_id;
                                line_title = line_orders.line_title;
                            }
                        } else {
                            converted_operation = LinesOrdersMaps.converted_extra_operations.get(converted_order_uuid);
                            if (converted_operation !== undefined) {
                                line_uuid = converted_operation.line.uuid;
                                line_external_id = converted_operation.line.external_id;
                                line_title = converted_operation.line.title;
                            }
                        }

                        let line = order_lines_map.get(line_uuid);
                        if (line === undefined) {
                            line = {
                                line_uuid,
                                line_external_id,
                                line_title,
                                orders: [],
                            };

                            order_lines_map.set(line_uuid, line);
                        }

                        const new_operation = deepClone(operation);
                        new_operation.order_uuid = converted_order_uuid;
                        new_operation.order_external_id = converted_order_external_id;
                        line.orders.push(new_operation);
                    }
                }
            }

            if (lines === converted_related_lines.input_lines) {
                converted_related_lines.input_lines = [...order_lines_map.values()];
            } else {
                converted_related_lines.output_lines = [...order_lines_map.values()];
            }

            order_lines_map.clear();
        }

        return converted_related_lines;
    };

    static getRelatedLines = async (order_uuid: string): Promise<t.IRelatedLinesForOrder> => {
        let order_uuid_mapped = order_uuid;
        const is_converted_operation = LinesOrdersLogic.isConvertedOperation(order_uuid_mapped);

        // check if the operation is not in the related lines map
        if (!ReduxFilters.related_lines_cache.has(order_uuid_mapped)) {
            // check if the opeartion is a chunk operation
            if (LinesOrdersMaps.chunk_orders.has(order_uuid)) {
                const chunk_order = LinesOrdersMaps.chunk_orders.get(order_uuid);
                if (chunk_order && chunk_order.parent_order_uuid) {
                    // set order_uuid to chunk parent uuid
                    order_uuid_mapped = chunk_order.parent_order_uuid;
                }
            }

            // check if the operation is a converted operation
            if (is_converted_operation) {
                order_uuid_mapped = LinesOrdersLogic.getConvertedOperationParentId(order_uuid_mapped);
            }
        }

        let related_lines = ReduxFilters.related_lines_cache.get(order_uuid_mapped);
        if (!related_lines) {
            const res = await getBackend2().manufacturing.getOperationRelatedLines({
                uuid: order_uuid_mapped,
            });
            related_lines = res.related_lines;
            ReduxFilters.related_lines_cache.set(order_uuid_mapped, related_lines);
        }

        if (is_converted_operation && !ReduxFilters.related_lines_cache.has(order_uuid)) {
            related_lines = ReduxFilters.fixRelatedLinesForConvertedOperation(related_lines as t.IRelatedLinesForOrder);
            ReduxFilters.related_lines_cache.set(order_uuid, related_lines);
        }

        return related_lines as t.IRelatedLinesForOrder;
    };

    static filterStockRequirements = (
        lines_orders: LineOrders[],
        unscheduled_orders: LineOrders[],
        stock_requirements: StockRequirement[],
        orders_filters: PlanningTableFiltersState,
        stock_requirements_filters: StockRequirementsFiltersState,
        insights_map_stock_requirements: InsightsMapStockRequirements
    ): StockRequirement[] => {
        let filtered_materials: string[] = [];
        const reschedule_order_uuid = orders_filters.reschedule_order_uuid;

        if (reschedule_order_uuid) {
            const order: IOrderProducedModelEx | null =
                LinesOrdersLogic.findOrder(lines_orders, reschedule_order_uuid) ||
                LinesOrdersLogic.findOrder(unscheduled_orders, reschedule_order_uuid);

            if (order) {
                filtered_materials = [order.material_external_id];
            }
        } else if (orders_filters.selected_materials.length > 0) {
            filtered_materials = orders_filters.selected_materials;
        }

        if (stock_requirements_filters.has_insights_filter) {
            stock_requirements = stock_requirements.filter(sr => insights_map_stock_requirements.has(sr.uuid));
        }

        if (filtered_materials.length === 0) {
            return stock_requirements;
        }

        return stock_requirements.filter(sr => filtered_materials.includes(sr.material_external_id));
    };
}

export const reduxRecommendPlan = RecommendPlanLogic.load;

export type OnOrderClickArgsActionTypes =
    | SetAdvancedOrderMenuAction
    | SetRescheduleOrderAction
    | ResetAdvancedOrderMenu
    | ResetFiltersAction
    | SetOrderTableProductionModalAction
    | SetSkipStockReport
    | SetPlanningDetailsSelectedView
    | SetHighlightedOrders
    | SetLastClickedOrderUuid
    | SetRescheduleOrderBundleAction
    | ClearMultiselectOrdersAction
    | UpdateChunkedOrderAction
    | ReportLoadingAction;

export type OnOrderClickArgs = {
    order_uuid: string;
    order_external_id: string;
    source_type: GanttChartSourceTypes;
    is_extra_line: boolean;
    is_ctrl_down: boolean;
    advanced_order_menu: AdvancedOrderMenuState | null;
    process_num: string;
    reduxDispatch: Dispatch<OnOrderClickArgsActionTypes | SetErrorAction>;
    is_readonly: boolean;
    is_filter_locked: boolean;
    is_unscheduled?: boolean;
    is_multiselected?: boolean;
    is_linked_operation?: boolean;
};

export type StockRequirementActions =
    | SetSelectedStockRequirement
    | UpdateStockRequirements
    | UpdateStockRequirementsHeightAction
    | SetSelectedLineUuidsAction
    | SetHighlightedOrders
    | SetRescheduleOrderAction
    | SetPlanningDetailsSelectedView
    | ReportLoadingAction
    | SetStockReqFilterMaterials
    | ResetFiltersAction;

export class UserActions {
    static onStockRequirementClick = async (
        uuid: string,
        material_external_id: string,
        lines_orders: LineOrders[],
        reduxDispatch: (args: StockRequirementActions) => void,
        same_selected: boolean
    ) => {
        if (same_selected) {
            reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: true });
            // when resetting filters rendering all the elements takes some time
            // with timeout the user will get whiteout sooner
            setTimeout(() => {
                if (!ReduxFilters.isLineLocked()) {
                    reduxDispatch({ type: RESET_FILTERS, data: undefined });
                }
                reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
            }, 5);
            return;
        }
        reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: true });
        reduxDispatch({ type: SET_SELECTED_STOCK_REQUIREMENT, data: uuid });
        reduxDispatch({ type: SET_STOCK_REQ_FILTERS_MATERIALS, data: [material_external_id] });
        reduxDispatch({ type: SET_PLANNING_DETAILS_SELECTED_VIEW, data: "stock_requirements" });

        const rect = document.querySelector(`[name='stock-requirements-rect'][data-uuid='${uuid}']`);

        if (rect) {
            setTimeout(() => {
                rect.scrollIntoView({ block: "center", inline: "center" });
            }, 500);
        }

        // filter gantt chart lines
        // filter unscheduled orders
        // highlight orders that have insights based on the stock requirement id
        const plant_uuid = local_settings.getItem(FIELD_LAST_PLANT);
        if (!plant_uuid) {
            return null;
        }

        getBackend2()
            .manufacturing.getSingleStockRequirementsShifts({
                plant_uuid,
                stock_requirement_uuid: uuid,
            })
            .then(res => {
                const line_uuids = new Set<string>();
                for (const sr of res.stock_requirements) {
                    for (const line_uuid of sr.line_uuids || []) {
                        line_uuids.add(line_uuid);
                    }
                }

                const highlighted_orders_map: Map<string, OrderHighlights> = new Map();
                for (const line of lines_orders) {
                    for (const order of line.orders) {
                        if (order.material_external_id === material_external_id) {
                            highlighted_orders_map.set(order.uuid, { is_highlighted: true });
                        }
                    }
                }

                reduxDispatch({ type: SET_HIGHLIGHTED_ORDERS, data: { highlighted_orders_map } });
                if (!ReduxFilters.isLineLocked()) {
                    reduxDispatch({ type: SET_SELECTED_LINE_UUIDS, data: [...line_uuids] });
                }
                reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
            });
    };

    static onOrderClick = async (args: OnOrderClickArgs) => {
        const {
            order_uuid,
            order_external_id,
            source_type,
            is_extra_line,
            is_ctrl_down,
            advanced_order_menu,
            process_num,
            reduxDispatch,
            is_readonly,
            is_filter_locked,
            is_multiselected = false,
        } = args;

        const is_planning_table =
            source_type === GanttChartSource.gantt_unscheduled ||
            source_type === GanttChartSource.planning_table ||
            source_type === GanttChartSource.planning_table_parallel;

        const is_linked_operation = LinesOrdersMaps.rework_uuid_base_order.has(order_uuid);
        if ((!is_planning_table || is_extra_line || is_readonly) && !is_linked_operation) {
            reduxDispatch({
                type: SET_ORDER_TABLE_PRODUCTION_MODAL,
                data: {
                    show_modal: true,
                    order_external_id,
                    order_uuid,
                    order_process_num: process_num,
                },
            });
        } else if (is_ctrl_down) {
            if (advanced_order_menu) {
                reduxDispatch({ type: SET_ADVANCED_ORDER_MENU, data: advanced_order_menu });
            }
        } else if (!is_multiselected) {
            reduxDispatch({ type: CLEAR_MULTISELECT_ORDERS, data: undefined });
            const order = LinesOrdersMaps.all_orders_map.get(order_uuid);
            if (order) {
                reduxDispatch({
                    type: REPORT_LOADING_ACTION_NAME,
                    data: `${order.material_title} - ${translate("common.processing_related_operations")} `,
                });
            }
            try {
                await ReduxFilters.setRescheduleOrderUuid(reduxDispatch, order_uuid, "gantt", is_filter_locked);
            } catch (error) {
                console.error("setRescheduleOrderUuid: ", error);
                reduxDispatch({ type: SET_ERROR, data: (error as Error).message });
            } finally {
                reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
            }
        }
    };
}

export class DraggableLogic {
    static querySelector = (source_type: string, selector: string) => {
        return document.querySelector(`#${source_type} ${selector}`);
    };

    static querySelectorAll = (source_type: string, selector: string) => {
        return document.querySelectorAll(`#${source_type} ${selector}`);
    };

    static changeMouseCursor = (cursor: CursorKeys | null) => {
        const new_event = new CustomEvent(SET_MOUSE_CURSOR, { detail: { key: cursor } });
        document.dispatchEvent(new_event);
    };
}

const robust_get_quantile = (arr: rt.ISimulationReportOrderQuantileData[], idx: number): number | null => {
    if (arr == undefined || arr.length == 0) {
        return null;
    }
    if (arr.length <= idx) {
        return arr[0].val;
    }
    return arr[idx].val;
};

export type ReportOrderEpochs = {
    initial_epoch_start: number | null;
    initial_epoch_end: number | null;
};

// Calculates initial start and end of the operation
// based on the initial shift time and order.
export const calculateReportOrderEpochs = (
    order: t2.ISimulationReportOrderEx,
    intial_shift: rt.ISimulationReportShiftTime,
    quantile_idx: number
): ReportOrderEpochs => {
    const initial_epoch_offset = dateFromWeekAndShift(
        intial_shift.week,
        intial_shift.year,
        intial_shift.shift_number
    ).getTime();
    const initial_hours_start = robust_get_quantile(order.estimated_start, quantile_idx);
    let initial_epoch_start: number | null = null;
    if (initial_hours_start !== null) {
        initial_epoch_start = initial_hours_start * TIME_RANGES.HOUR + initial_epoch_offset;
    }

    let initial_epoch_end: number | null = null;
    const initial_hours_end = robust_get_quantile(order.estimated_completion, quantile_idx);
    if (initial_hours_end !== null) {
        initial_epoch_end = initial_hours_end * TIME_RANGES.HOUR + initial_epoch_offset;
    }
    return { initial_epoch_start, initial_epoch_end };
};

export const getPreviousOperationEpochs = (
    report_data: t2.IReportModelEx | null,
    order_external_id: string,
    max_process_num: number
): ReportOrderEpochs | null => {
    if (!report_data) {
        return null;
    }

    let max_order: t2.ISimulationReportOrderEx | null = null;
    for (const line of report_data.result.orders) {
        for (const order of line.production) {
            const order_process_num = parseFloat(order.process_num);
            if (order.order_external_id === order_external_id) {
                if (order_process_num < max_process_num) {
                    if (!max_order) {
                        max_order = order;
                        continue;
                    } else if (order_process_num > parseFloat(max_order.process_num)) {
                        max_order = order;
                    }
                }
            }
        }
        if (max_order) {
            return calculateReportOrderEpochs(max_order, report_data.result.next_shift_time, 1);
        }
    }
    return null;
};

export type PlanChange = {
    operation_uuid: string;
    initial_line_index: number;
    initial_operation_index: number;
    initial_scheduled_state: boolean;
    final_line_index: number;
    final_operation_index: number;
    final_scheduled_state: boolean;
    time_start?: number;
};

type OrderWithPlanChange = {
    index: number;
    uuid: string;
    new_time_start: number;
    time_end: number;
    plan_change?: PlanChange;
};

/**
 * Calculates the changes in the plan for an operation during scheduling, such as moving an operation's position
 * or changing its start time. Stores initial and final line and operation indices,
 * the scheduled state before and after the change, and optionally the new start time.
 */
export const preparePlanChanges = (
    leading_order_uuid: string,
    leading_order_time_start: number,
    selected_orders_uuids: string[],
    report: t2.IReportModelEx,
    line: LineOrders,
    line_index: number
): PlanChange[] => {
    // Prepare plan changes array, initially final_oparation_index and initial_operation_index are the same
    const plan_changes = line.orders
        .map((order, order_index) => {
            if (selected_orders_uuids.includes(order.uuid)) {
                return {
                    operation_uuid: order.uuid,
                    initial_line_index: line_index,
                    initial_operation_index: order_index,
                    initial_scheduled_state: true,
                    final_line_index: line_index,
                    final_operation_index: order_index,
                    final_scheduled_state: true,
                };
            }
        })
        // Remove undefined values
        .filter(Boolean) as PlanChange[];

    // Prepare simulation operations map for quick access
    const sim_operations_map: Map<string, t2.ISimulationReportOrderEx> = new Map();
    for (const line of report.result.orders) {
        for (const order of line.production) {
            sim_operations_map.set(order.order_id, order);
        }
    }

    // Get timestamp of first shift, i.e. start of gantt chart
    // report.result.next_shift_time is actually the first shift time
    const gantt_first_shift_time = report.result.next_shift_time;
    const min_time_start = dateFromWeekAndShift(
        gantt_first_shift_time.week,
        gantt_first_shift_time.year,
        gantt_first_shift_time.shift_number
    ).getTime();

    // Get time of leading operation (new start time compared to start time from simulation)
    const leading_sim_operation = sim_operations_map.get(leading_order_uuid);
    let time_diff = 0;
    if (leading_sim_operation !== undefined) {
        // Time start before dragging
        const { initial_epoch_start: leading_operation_initial_time_start } = calculateReportOrderEpochs(
            leading_sim_operation,
            gantt_first_shift_time,
            1
        );
        if (leading_operation_initial_time_start !== null) {
            // leading_order_time_start - time start after dragging
            time_diff = leading_order_time_start - leading_operation_initial_time_start;
        }
    }

    // Calculate new indices for each operation, shifted by time_diff
    const orders_with_plan_changes: OrderWithPlanChange[] = line.orders.map((order, index) => {
        let new_time_start = 0;
        let time_end = 0;

        const uuid = order.uuid;
        const sim_operation = sim_operations_map.get(uuid);
        const plan_change = plan_changes.find(({ operation_uuid }) => operation_uuid === uuid);

        // Calculate new time start for the order (shifted by time_diff)
        if (sim_operation !== undefined) {
            const quantile_idx = 1;
            const epoch = calculateReportOrderEpochs(sim_operation, gantt_first_shift_time, quantile_idx);

            if (epoch.initial_epoch_end !== null) {
                time_end = epoch.initial_epoch_end;
            }

            if (plan_change) {
                new_time_start = Math.max(min_time_start, (epoch.initial_epoch_start ?? min_time_start) + time_diff);
            }
        }

        return {
            index,
            uuid,
            new_time_start,
            time_end,
            plan_change,
        };
    });

    const first_selected_order = orders_with_plan_changes.find(({ plan_change }) => plan_change);
    if (!first_selected_order) return [];

    // Find the last order (operation) that ends before first_selected_order new time start
    const last_order_before_first_selected = orders_with_plan_changes
        .filter(({ uuid }) => uuid !== first_selected_order.uuid)
        .findLast(({ time_end }) => time_end <= first_selected_order.new_time_start);

    // If it's is not found, it means the selected orders are moved to the beggining of the line
    let insertion_index = last_order_before_first_selected?.index ?? -1;

    // We assume that the selected orders are consecutive,
    // so we can increment the index for each selected order
    for (const { plan_change } of orders_with_plan_changes) {
        if (plan_change) {
            plan_change.final_operation_index = ++insertion_index;
        }
    }

    // If the selected orders are not really moved, return an empty array
    if (
        plan_changes.every(
            ({ initial_operation_index, final_operation_index }) => initial_operation_index === final_operation_index
        )
    ) {
        return [];
    }

    return plan_changes;
};

export const disableUserSelect = () => {
    const els = document.querySelectorAll("#scheduled-orders,#unscheduled-orders");
    for (const el of els) {
        if (el && !el.classList.contains("disable-user-events")) {
            el.classList.add("disable-user-events");
        }
    }

    if (document.body) {
        document.body.classList.add("user-select-none");
    }
};

export const enableUserSelect = () => {
    const els = document.querySelectorAll("#scheduled-orders,#unscheduled-orders");
    for (const el of els) {
        if (el && el.classList.contains("disable-user-events")) {
            el.classList.remove("disable-user-events");
        }
    }

    if (document.body) {
        document.body.classList.remove("user-select-none");
    }
};

export class ReportLoadingEventHandler {
    static EVENT = "report-loading-event" as const;

    static showLoading = (show: boolean) => {
        const e = new CustomEvent(ReportLoadingEventHandler.EVENT, {
            detail: {
                loading: show,
            },
        });
        window.dispatchEvent(e);
    };
}

export class TypeAdapter {
    /**
     * Transforms an `IOrderProducedModelEx` object into an `IOrderProducedModel`.
     * Removes the fileds that exist in IOrderProducedModelEx, but not in IOrderProducedModel.
     * */
    static transformOrderProducedModelFromEx = ({
        has_production, // This field gets removed
        unscheduled_highlighted, // This field gets removed
        unscheduled_selected, // This field gets removed
        failed_scheduling, // This field gets removed
        line_title, // This field gets removed
        ...transformedOrder // Capture all remaining properties
    }: IOrderProducedModelEx): t.IOrderProducedModel => {
        return {
            ...transformedOrder,
            last_prod_shift_uuid: "",
            produced_available_in_hours: 0,
        };
    };

    /**
     * Transforms an `IOrderProducedModel` object into an `IOrderProducedModelEx` object by cloning the input
     * and setting `IOrderProducedModelEx` specific properties to `undefined`.
     */
    static transformOrderProducedModelToEx = (order: t.IOrderProducedModel): IOrderProducedModelEx => {
        const cloned = {
            ...deepClone(order),
            has_production: undefined,
            unscheduled_highlighted: undefined,
            unscheduled_selected: undefined,
            failed_scheduling: undefined,
            estimated_completion: undefined,
            estimated_duration: undefined,
            line_title: undefined,
        };
        return cloned;
    };

    /** Sets the `is_disabled` property to `false` for each order in report.result.orders property. */
    static transformReportModelToEx = (report: rt.IReportModel): t2.IReportModelEx => {
        const cloned = deepClone(report);
        const orders = cloned.result.orders.map(order => {
            return {
                ...order,
                is_disabled: false,
            };
        });
        cloned.result.orders = orders;
        return cloned as t2.IReportModelEx;
    };

    /** Removes the `is_disabled` property for each order in report.result.orders property. */
    static transformReportModelFromEx = (report: t2.IReportModelEx): rt.IReportModel => {
        const cloned_report = deepClone(report);
        const orders: t2.ISimulationReportOrderLineEx[] = cloned_report.result.orders.map(order => {
            const cloned_order = { ...order };
            delete cloned_order.is_disabled;
            return cloned_order;
        });
        cloned_report.result.orders = orders;
        return cloned_report;
    };
}
